<template>
  <v-container fluid class="px-0">
    <v-row class="mt-3 px-6" v-if="!showEditDefinition">
      <v-col class="d-flex align-center">
        <h1 class="title">Landing Page Configuration</h1>
      </v-col>
    </v-row>

    <v-row class="px-6" v-if="!showEditDefinition">
      <v-col>
        <v-card width="100%" class="py-4">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="py-2"><h2>Page</h2></th>
                  <th class="py-2">
                    <h2>Actions</h2>
                  </th>
                  <th class="py-2" colspan="20">
                    <h2>Permission Groups</h2>
                  </th>
                </tr>
                <tr>
                  <th colspan="2"></th>
                  <th v-for="pg in permissionGroups" :key="pg.pg_id">
                    <v-chip small class="mb-2">{{
                      pg.permission_group_name
                    }}</v-chip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="p in landingPages">
                  <tr
                    :key="'page' + p.lp_id"
                    :class="!p.active ? 'inactive' : ''"
                  >
                    <td>
                      <v-icon v-if="p.icon" :class="p.icon">{{
                        p.icon ? p.icon.split(" ")[0] : ""
                      }}</v-icon>
                      {{ p.menu_section ? `${p.menu_section} -` : "" }}
                      {{ p.title }}
                    </td>
                    <td>
                      <v-btn icon title="Configure Page" @click="editPage(p)">
                        <v-icon>settings</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        title="Add New Blank Definition"
                        @click="addDefinition(p)"
                      >
                        <v-icon>add_circle_outline</v-icon>
                      </v-btn>
                    </td>
                    <td colspan="20"></td>
                  </tr>
                  <template v-for="pd in p.definitions">
                    <tr
                      :key="'pageDef_' + pd.lpd_id"
                      :class="!p.active || !pd.active ? 'inactive' : ''"
                    >
                      <td>
                        <v-icon class="ml-6">subdirectory_arrow_right</v-icon
                        >{{ pd.description }}
                      </td>
                      <td>
                        <v-btn
                          icon
                          title="Delete Definition"
                          @click="deleteDefinition(p, pd)"
                        >
                          <v-icon>delete</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          title="Copy Definition"
                          @click="addDefinition(p, pd)"
                        >
                          <v-icon>content_copy</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          title="Upload Definition"
                          @click="uploadDefinition(p, pd)"
                        >
                          <v-icon>file_upload</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          title="Download Definition"
                          @click="downloadDefinition(p, pd)"
                        >
                          <v-icon>file_download</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          title="Edit Definition"
                          @click="openDefinitionForEdit(p, pd)"
                        >
                          <v-icon>edit</v-icon>
                        </v-btn>
                      </td>
                      <td v-for="pg in permissionGroups" :key="pg.pg_id">
                        <v-btn
                          icon
                          title="Disable"
                          v-if="pd[`pg${pg.permission_group_id}_enabled`]"
                          @click="toggleRoute(pd, pg.permission_group_id)"
                        >
                          <v-icon>mdi-check-circle-outline</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          title="Enable"
                          v-else
                          style="opacity: 0.2"
                          @click="toggleRoute(pd, pg.permission_group_id)"
                        >
                          <v-icon>mdi-plus-outline</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="20" class="pt-4">
                    <v-btn color="primary" outlined @click="addPage"
                      >Add New Page</v-btn
                    >
                  </td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="showEditDefinition && pageDefinition">
      <v-col>
        <v-row class="px-6">
          <v-col cols="4">
            <v-btn @click="closeDefinition" color="primary" outlined>
              <v-icon left>mdi-arrow-left</v-icon>Back</v-btn
            >
          </v-col>
          <v-col cols="4" class="d-flex justify-center align-center">
            <h2 class="mr-3">
              {{ pageDefinition.pageTitle }} / {{ pageDefinition.description }}
              {{ !pageDefinition.active ? "(disabled)" : "" }}
              <v-btn icon @click="configureDefinition">
                <v-icon>settings</v-icon>
              </v-btn>
            </h2>
            <!-- Engine switch — render this page with the new (v3) dashboard
                 engine: nested rows/cols + the visual row editor below. -->
            <v-switch
              class="mt-0 pt-0"
              hide-details
              dense
              label="v3 engine"
              :input-value="pageDefinition.definition.engine === 'v3'"
              @change="(val) => $set(pageDefinition.definition, 'engine', val ? 'v3' : undefined)"
            ></v-switch>
          </v-col>
          <v-col cols="4" class="d-flex justify-end">
            <v-btn class="ml-3" color="primary" outlined @click="addTab"
              >Add Tab</v-btn
            >
            <v-btn v-if="!isV3Engine" class="ml-3" color="primary" outlined @click="addColumn"
              >Add Column</v-btn
            >
            <v-btn v-if="!isV3Engine" class="ml-3" color="primary" outlined @click="addRow"
              >Add Control</v-btn
            >
            <v-btn class="ml-3" color="primary" @click="saveDefinition"
              >Save</v-btn
            >
          </v-col>
        </v-row>
        <div class="ma-3">
          <!-- New (v3) engine → the visual rows/columns editor. -->
          <V3Editor
            v-if="isV3Engine"
            :rows="v3Rows"
            :qaActions="[]"
            :controlTypes="uiControlTypes"
            @add-row="v3AddRow"
            @delete-row="v3DeleteRow"
            @add-column="v3AddColumn"
            @delete-column="v3DeleteColumn"
            @set-span="v3SetSpan"
            @add-control="v3AddControl"
            @edit-control="rowEdit"
            @delete-control="v3DeleteControl"
          />
          <Landing
            v-else
            v-model="pageDefinition.definition"
            :editMode="true"
            @tabEdit="tabEdit"
            @tabMoveLeft="tabMoveLeft"
            @tabMoveRight="tabMoveRight"
            @deleteTab="deleteTab"
            @changeActiveTab="changeActiveTab"
            @colEdit="colEdit"
            @deleteCol="deleteCol"
            @rowEdit="rowEdit"
            @deleteRow="deleteRow"
            @editTile="editTile"
            @addTile="addTile"
            @copyTile="copyTile"
            @deleteTile="deleteTile"
          >
          </Landing>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="showPageEdit" max-width="500px">
      <v-card v-if="page">
        <v-card-title>
          <div>Page Settings</div>
          <v-spacer />
          <v-btn icon @click="showPageEdit = false"
            ><v-icon>close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="pb-0">
          <v-row>
            <v-col cols="3">
              <v-text-field
                label="Position"
                type="number"
                v-model="page.order"
              ></v-text-field>
            </v-col>

            <v-col cols="6">
              <v-text-field
                label="Route"
                v-model="page.page_route"
              ></v-text-field>
            </v-col>

            <v-col cols="3" class="d-flex justify-end">
              <v-switch
                label="Active"
                v-model="page.active"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4">
              <v-select
                label="Menu Section"
                v-model="page.menu_section"
                :items="['Main', 'Reporting', 'Admin', 'Hidden']"
              >
              </v-select>
            </v-col>
            <v-col cols="8">
              <v-text-field label="Title" v-model="page.title"></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <div class="d-flex justify-space-between">
                <v-icon :class="page.icon" style="width: 25px">{{
                  page.icon ? page.icon.split(" ")[0] : ""
                }}</v-icon>
                <v-text-field
                  class="ml-3"
                  label="Icon"
                  v-model="page.icon"
                ></v-text-field>
              </div>
            </v-col>
            <v-col class="d-flex align-center">
              <v-switch
                label="Hide Header Create"
                v-model="page.hide_header_create"
              ></v-switch>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="d-flex justify-end pb-4 px-6">
          <v-btn color="primary" @click="savePage"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDefinitionEdit" max-width="500px">
      <v-card v-if="pageDefinition">
        <v-card-title>
          Definition Settings
          <v-spacer />
          <v-btn icon @click="showDefinitionEdit = false"
            ><v-icon>close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Description"
            v-model="pageDefinition.description"
          ></v-text-field>

          <v-select
            v-if="tabSelect"
            label="Default Tab"
            v-model="pageDefinition.definition.defaultTabIndex"
            :items="tabSelect"
            item-text="title"
            item-value="index"
          >
          </v-select>

        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showTabEdit" max-width="500px">
      <v-card v-if="tabModel">
        <v-card-title>
          Tab Settings
          <v-spacer />
          <v-btn icon @click="showTabEdit = false"
            ><v-icon>close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-text-field
                label="Title"
                v-model="tabModel.title"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showColEdit" max-width="500px">
      <v-card v-if="colModel">
        <v-card-title>
          Column Settings
          <v-spacer />
          <v-btn icon @click="showColEdit = false"
            ><v-icon>close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <v-text-field
                label="Columns"
                type="number"
                v-model="colModel.cols"
              ></v-text-field>
            </v-col>
            <v-col cols="8">
              <v-text-field
                label="Margin"
                type="number"
                v-model="colModel.margin"
                hint="Horizontal applies to the tab's outer edges only (no doubling between cols). Vertical applies to every col."
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showRowEdit"
      :max-width="
        rowModel && (
          rowModel.controlType.startsWith('DocumentList') ||
          rowModel.controlType === 'DonutChart' ||
          rowModel.controlType === 'BarChart' ||
          rowModel.controlType === 'v3BarChart'
        ) ? '1200px' : '800px'
      "
    >
      <v-card v-if="rowModel">
        <v-card-title>
          Control Settings
          <v-spacer />
          <v-btn icon @click="showRowEdit = false"
            ><v-icon>close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-row>
                <v-col cols="9">
                  <v-select
                    label="Control Type"
                    v-model="rowModel.controlType"
                    :items="uiControlTypes"
                    item-text="text"
                    item-value="value"
                    @change="onControlTypeChange"
                  >
                  </v-select>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    label="Columns"
                    v-model="rowModel.cols"
                    type="number"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="!['DonutChart', 'BarChart', 'v3DonutChart', 'v3BarChart'].includes(rowModel.controlType)">
                <v-col cols="9">
                  <v-select
                    label="Style"
                    :items="controlStyle"
                    v-model="rowModel.style"
                    item-text="text"
                    item-value="value">
                  </v-select>
                </v-col>
                <v-col cols="3">
                  <v-switch
                    label="Contained"
                    v-model="rowModel.contained"
                  >
                  </v-switch>
                </v-col>
              </v-row>
              

              <v-text-field
                label="Title"
                v-model="rowModel.title"
              ></v-text-field>

              <!-- New-portal (v3) Hero widget settings. Stored as plain row
                   fields; mapped to the beta Hero widget's props in
                   rm-portal-beta (definitionMapper.ts). -->
              <template v-if="rowModel.controlType === 'v3Hero'">
                <v-switch
                  :input-value="rowModel.resume !== false"
                  @change="(v) => $set(rowModel, 'resume', v)"
                  label="Resume mode (show the user's in-progress evaluation)"
                  hint="On → surfaces the in-progress evaluation with progress + Continue; falls back to the content below when nothing is in progress."
                  persistent-hint
                  dense
                ></v-switch>
                <v-textarea
                  label="Subtitle"
                  v-model="rowModel.subtitle"
                  rows="2"
                  auto-grow
                ></v-textarea>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      label="Icon (Material Symbols name)"
                      v-model="rowModel.icon"
                      placeholder="rocket_launch"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      label="Icon Colour"
                      v-model="rowModel.iconVariant"
                      :items="['accent', 'violet', 'amber', 'rose', 'green', 'neutral']"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      label="CTA Label"
                      v-model="rowModel.ctaLabel"
                      placeholder="Create"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-combobox
                      label="CTA Action"
                      v-model="rowModel.ctaAction"
                      :items="ctaActionItems"
                      item-text="label"
                      item-value="path"
                      :return-object="false"
                      placeholder="create"
                      hint="'create' opens the create dialog; otherwise a route"
                      persistent-hint
                    ></v-combobox>
                  </v-col>
                </v-row>
              </template>

              <!-- New-portal (v3) Empty State — a placeable "you're all caught
                   up" panel. The Title above is the heading; icon + message +
                   optional CTA below. Mapped to the beta EmptyState widget. -->
              <template v-if="rowModel.controlType === 'v3Empty'">
                <v-textarea
                  label="Message"
                  v-model="rowModel.message"
                  rows="2"
                  auto-grow
                  placeholder="Nothing needs your attention right now."
                ></v-textarea>
                <v-text-field
                  label="Icon (Material Symbols name)"
                  v-model="rowModel.icon"
                  placeholder="task_alt"
                ></v-text-field>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      label="Button Label (blank = no button)"
                      v-model="rowModel.ctaLabel"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-combobox
                      label="Button Action"
                      v-model="rowModel.ctaAction"
                      :items="ctaActionItems"
                      item-text="label"
                      item-value="path"
                      :return-object="false"
                      hint="'create' opens the create dialog; otherwise a route"
                      persistent-hint
                    ></v-combobox>
                  </v-col>
                </v-row>
              </template>

              <!-- New-portal (v3) Quick Links — a heading (the Title above) plus
                   an addable list of tiles, each with a label, icon and
                   destination. Stored on `rowModel.tiles`; mapped to the beta
                   QuickLinks widget's `tiles` prop. -->
              <template v-if="rowModel.controlType === 'v3QuickLinks'">
                <div class="d-flex align-center mt-3 mb-1">
                  <h3 class="subtitle-2">Tiles</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="addV3Tile(rowModel)">
                    <v-icon left small>add</v-icon>Add tile
                  </v-btn>
                </div>
                <v-card
                  v-for="(t, ti) in (rowModel.tiles || [])"
                  :key="'v3tile' + ti"
                  outlined
                  class="mb-2 pa-2"
                >
                  <v-row dense align="center">
                    <v-col cols="3">
                      <v-text-field label="Label" v-model="t.label" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-text-field
                        label="Icon"
                        v-model="t.icon"
                        placeholder="description"
                        dense
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-select
                        label="Colour"
                        v-model="t.tone"
                        :items="v3TileTones"
                        item-text="text"
                        item-value="value"
                        placeholder="Auto"
                        clearable
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="3">
                      <v-combobox
                        label="Destination"
                        v-model="t.destination"
                        :items="internalRoutes"
                        item-text="label"
                        item-value="path"
                        :return-object="false"
                        placeholder="route or URL"
                        dense
                        hide-details
                      ></v-combobox>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        label="Detail line (optional)"
                        v-model="t.sub"
                        placeholder="e.g. 128 live"
                        dense
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="2" class="d-flex justify-end">
                      <v-btn icon small :disabled="ti === 0" @click="moveV3Tile(rowModel, ti, -1)">
                        <v-icon small>arrow_upward</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        :disabled="ti === (rowModel.tiles.length - 1)"
                        @click="moveV3Tile(rowModel, ti, 1)"
                      >
                        <v-icon small>arrow_downward</v-icon>
                      </v-btn>
                      <v-btn icon small @click="removeV3Tile(rowModel, ti)">
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-1">
                    <v-col cols="6">
                      <v-select
                        label="Pre-set doc types on destination (optional)"
                        :value="t.docTypes || []"
                        @input="(val) => $set(t, 'docTypes', val)"
                        :items="v3DocTypeItems"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        clearable
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        label="Pre-set statuses on destination (optional)"
                        :value="t.statuses || []"
                        @input="(val) => $set(t, 'statuses', val)"
                        :items="v3StatusItems"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        clearable
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- New-portal (v3) Stat Row — an addable list of tiles, each
                   bound to a stat field (driven by the available count APIs),
                   with an icon, colour and optional label override. Stored on
                   `rowModel.tiles`; mapped to the beta StatRow widget. -->
              <template v-if="rowModel.controlType === 'v3StatRow'">
                <div class="d-flex align-center mt-3 mb-1">
                  <h3 class="subtitle-2">Tiles</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="addV3StatTile(rowModel)">
                    <v-icon left small>add</v-icon>Add tile
                  </v-btn>
                </div>
                <p class="caption grey--text mb-2" v-if="!(rowModel.tiles || []).length">
                  No tiles configured — the row shows per-document-type counts by default.
                </p>
                <v-card
                  v-for="(t, ti) in (rowModel.tiles || [])"
                  :key="'v3stat' + ti"
                  outlined
                  class="mb-2 pa-2"
                >
                  <v-row dense align="center">
                    <v-col cols="4">
                      <v-select
                        label="Stat field"
                        v-model="t.statField"
                        :items="statFieldItems"
                        item-text="label"
                        item-value="value"
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="3">
                      <v-text-field label="Label" v-model="t.label" placeholder="(auto)" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-text-field label="Icon" v-model="t.icon" placeholder="(auto)" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-select
                        label="Colour"
                        v-model="t.colour"
                        :items="v3StatTones"
                        item-text="text"
                        item-value="value"
                        placeholder="Auto"
                        clearable
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="1" class="d-flex justify-end">
                      <v-btn icon small @click="removeV3StatTile(rowModel, ti)">
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-1">
                    <v-col cols="12">
                      <v-combobox
                        label="Links to (optional)"
                        v-model="t.to"
                        :items="internalRoutes"
                        item-text="label"
                        item-value="path"
                        :return-object="false"
                        placeholder="route or URL — blank = not clickable"
                        hint="Makes the whole tile a button"
                        persistent-hint
                        dense
                        clearable
                      ></v-combobox>
                    </v-col>
                  </v-row>
                  <!-- Per-tile doc-type scope: narrows THIS tile's count to (or
                       away from) the selected types — so one tile can count a
                       status for Job Profiles and another for Job Descriptions.
                       Also presets the doc-type filter when the tile deep-links. -->
                  <v-row dense class="mt-1">
                    <v-col :cols="(t.docTypes || []).length ? 8 : 12">
                      <v-select
                        label="Scope to doc types (blank = all)"
                        v-model="t.docTypes"
                        :items="docTypes"
                        item-text="tmpl_name"
                        item-value="tmpl_name"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        clearable
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="4" v-if="(t.docTypes || []).length">
                      <v-select
                        label="Doc-type filter mode"
                        v-model="t.docTypesMode"
                        :items="[{ text: 'Include', value: 'include' }, { text: 'Exclude', value: 'exclude' }]"
                        item-text="text"
                        item-value="value"
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                  </v-row>
                  <!-- Per-tile status scope: narrows THIS tile's count to (or away
                       from) the selected statuses — e.g. a "Documents — Job
                       Profile" tile limited to certain lifecycle statuses. Also
                       presets the status filter when the tile deep-links. -->
                  <v-row dense class="mt-1">
                    <v-col :cols="(t.statuses || []).length ? 8 : 12">
                      <v-select
                        label="Scope to statuses (blank = all)"
                        v-model="t.statuses"
                        :items="v3StatusItems"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        clearable
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="4" v-if="(t.statuses || []).length">
                      <v-select
                        label="Status filter mode"
                        v-model="t.statusesMode"
                        :items="[{ text: 'Include', value: 'include' }, { text: 'Exclude', value: 'exclude' }]"
                        item-text="text"
                        item-value="value"
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- New-portal (v3) Feedback Strip — title (above) + subtitle +
                   an addable list of outcome items (title · detail · when ·
                   status). -->
              <template v-if="rowModel.controlType === 'v3FeedbackStrip'">
                <v-text-field label="Subtitle" v-model="rowModel.subtitle" placeholder="Outcomes land here"></v-text-field>
                <div class="d-flex align-center mt-2 mb-1">
                  <h3 class="subtitle-2">Items</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="addV3FbItem(rowModel)">
                    <v-icon left small>add</v-icon>Add item
                  </v-btn>
                </div>
                <v-card v-for="(it, ii) in (rowModel.items || [])" :key="'v3fb' + ii" outlined class="mb-2 pa-2">
                  <v-row dense align="center">
                    <v-col cols="3">
                      <v-text-field label="Title" v-model="it.title" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-text-field label="Detail" v-model="it.detail" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-text-field label="When" v-model="it.when" placeholder="2 days ago" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-select
                        label="Status"
                        v-model="it.status"
                        :items="v3FbStatuses"
                        item-text="text"
                        item-value="value"
                        dense
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="2" class="d-flex justify-end">
                      <v-btn icon small :disabled="ii === 0" @click="moveV3FbItem(rowModel, ii, -1)">
                        <v-icon small>arrow_upward</v-icon>
                      </v-btn>
                      <v-btn icon small :disabled="ii === (rowModel.items.length - 1)" @click="moveV3FbItem(rowModel, ii, 1)">
                        <v-icon small>arrow_downward</v-icon>
                      </v-btn>
                      <v-btn icon small @click="removeV3FbItem(rowModel, ii)">
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- New-portal (v3) Donut Chart — same definition shape as the
                   legacy DonutChart (static segments or API), rendered with the
                   v3 design styling. -->
              <template v-if="rowModel.controlType === 'v3DonutChart'">
                <v-select
                  label="Endpoint preset"
                  :value="rowModel.preset || ''"
                  :items="[{ text: '— Choose a preset (optional) —', value: '' }, ...donutPresets.map((p) => ({ text: p.label, value: p.id }))]"
                  item-text="text"
                  item-value="value"
                  hint="Stamps the full wiring for a shipped Report/* endpoint (endpoint, method, field mappings, filters, labels) in one click. Fine-tune below afterwards."
                  persistent-hint
                  class="mb-2"
                  @change="applyV3Preset(rowModel, donutPresets, $event)"
                ></v-select>
                <v-row>
                  <v-col cols="4">
                    <v-select
                      label="Display As"
                      v-model="rowModel.mode"
                      :items="[{ text: 'Donut', value: 'donut' }, { text: 'Stacked bar', value: 'bar' }]"
                      item-text="text"
                      item-value="value"
                    ></v-select>
                  </v-col>
                  <v-col cols="4">
                    <v-select
                      label="Data Source"
                      v-model="rowModel.dataSource"
                      :items="dataSources"
                      item-text="text"
                      item-value="value"
                    ></v-select>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      label="Centre Label"
                      v-model="rowModel.centreLabel"
                      placeholder="e.g. jobs"
                      :hint="docTypeTokenHint"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field label="Centre Value" v-model="rowModel.centreValue" placeholder="Auto (sum)"></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      label="Completed segment labels"
                      v-model="rowModel.doneLabels"
                      placeholder="e.g. Completed, Approved"
                      hint="Comma-separated segment labels that count as COMPLETE → shows the 'N% complete' insight in bar mode. Blank = no insight."
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      label="Default doc type"
                      v-model="rowModel.defaultType"
                      hint="Doc-type chip pre-selected when the type switcher shows. Blank = first value."
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      label="Link Text"
                      v-model="rowModel.linkText"
                      :placeholder="docTypeTokenPlaceholder"
                      :hint="docTypeTokenHint"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-combobox
                      label="Link URL"
                      v-model="rowModel.linkUrl"
                      :items="internalRoutes"
                      item-text="label"
                      item-value="path"
                      :return-object="false"
                    ></v-combobox>
                  </v-col>
                </v-row>

                <template v-if="rowModel.dataSource === 'api'">
                  <v-row>
                    <v-col cols="3">
                      <v-select label="Method" v-model="rowModel.apiMethod" :items="['GET', 'POST']"></v-select>
                    </v-col>
                    <v-col cols="9">
                      <v-text-field label="Endpoint" v-model="rowModel.apiEndpoint" placeholder="report/evaluationProgress"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <v-text-field label="Label Field" v-model="rowModel.segmentMapping.labelField"></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field label="Value Field" v-model="rowModel.segmentMapping.valueField"></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field label="Colour Field" v-model="rowModel.segmentMapping.colourField" placeholder="optional"></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Filters passed into the API call (out.filters: [{field,
                       condition, values}]). Same shape the legacy DonutChart
                       sends. -->
                  <h3 class="subtitle-2 mt-2 mb-1">Filters</h3>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th style="width: 160px;">Field</th>
                        <th style="width: 130px;">Condition</th>
                        <th>Values</th>
                        <th style="width: 48px;">
                          <v-btn icon @click="addDonutApiFilter">
                            <v-icon>add_circle_outline</v-icon>
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody v-if="rowModel.apiFilters">
                      <tr v-for="(f, fi) in rowModel.apiFilters" :key="'v3daf' + fi">
                        <td>
                          <v-select
                            v-model="f.field"
                            :items="[
                              { text: 'Doc Type', value: 'docType' },
                              { text: 'Status', value: 'status' },
                            ]"
                            hide-details
                            dense
                          ></v-select>
                        </td>
                        <td>
                          <v-select
                            v-model="f.condition"
                            :items="['include', 'exclude']"
                            hide-details
                            dense
                          ></v-select>
                        </td>
                        <td>
                          <v-combobox
                            v-model="f.values"
                            multiple
                            small-chips
                            deletable-chips
                            hide-details
                          ></v-combobox>
                        </td>
                        <td>
                          <v-btn icon @click="rowModel.apiFilters = rowModel.apiFilters.filter(o => o !== f)">
                            <v-icon>delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <h3 class="subtitle-2 mt-3 mb-1">API Parameters</h3>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Value</th>
                        <th style="width: 48px;">
                          <v-btn icon @click="addApiParam(rowModel)">
                            <v-icon>add_circle_outline</v-icon>
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody v-if="rowModel.apiParams">
                      <tr v-for="(p, pi) in rowModel.apiParams" :key="'v3dp' + pi">
                        <td><v-text-field v-model="p.name" hide-details></v-text-field></td>
                        <td><v-text-field v-model="p.value" hide-details></v-text-field></td>
                        <td>
                          <v-btn icon @click="rowModel.apiParams = rowModel.apiParams.filter(o => o !== p)">
                            <v-icon>delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <h3 class="subtitle-2 mt-3 mb-1">Colour Overrides</h3>
                  <div class="caption text--secondary mb-1">
                    Optional — map a label returned by the API to a specific colour.
                  </div>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th style="width: 160px;">Colour</th>
                        <th style="width: 48px;">
                          <v-btn icon @click="addDonutColourOverride">
                            <v-icon>add_circle_outline</v-icon>
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody v-if="rowModel.segmentColours">
                      <tr v-for="(c, ci) in rowModel.segmentColours" :key="'v3sc' + ci">
                        <td><v-text-field v-model="c.label" hide-details></v-text-field></td>
                        <td>
                          <v-combobox v-model="c.colour" :items="hexColours" hide-details dense>
                            <template v-slot:prepend>
                              <v-icon :color="c.colour">circle</v-icon>
                            </template>
                          </v-combobox>
                        </td>
                        <td>
                          <v-btn icon @click="rowModel.segmentColours = rowModel.segmentColours.filter(o => o !== c)">
                            <v-icon>delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </template>

                <template v-else>
                  <div class="d-flex align-center mt-2 mb-1">
                    <h3 class="subtitle-2">Segments</h3>
                    <v-spacer></v-spacer>
                    <v-btn small text color="primary" @click="addV3Segment(rowModel)">
                      <v-icon left small>add</v-icon>Add segment
                    </v-btn>
                  </div>
                  <v-card
                    v-for="(s, si) in (rowModel.segments || [])"
                    :key="'v3seg' + si"
                    outlined
                    class="mb-2 pa-2"
                  >
                    <v-row dense align="center">
                      <v-col cols="4">
                        <v-text-field label="Label" v-model="s.label" dense hide-details></v-text-field>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field label="Value" v-model.number="s.value" type="number" dense hide-details></v-text-field>
                      </v-col>
                      <v-col cols="3">
                        <v-select
                          label="Colour"
                          v-model="s.tone"
                          :items="v3TileTones"
                          item-text="text"
                          item-value="value"
                          placeholder="Auto"
                          clearable
                          dense
                          hide-details
                        ></v-select>
                      </v-col>
                      <v-col cols="2" class="d-flex justify-end">
                        <v-btn icon small :disabled="si === 0" @click="moveV3Segment(rowModel, si, -1)">
                          <v-icon small>arrow_upward</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          :disabled="si === (rowModel.segments.length - 1)"
                          @click="moveV3Segment(rowModel, si, 1)"
                        >
                          <v-icon small>arrow_downward</v-icon>
                        </v-btn>
                        <v-btn icon small @click="removeV3Segment(rowModel, si)">
                          <v-icon small>delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </template>

              <v-row v-if="!['DonutChart', 'BarChart', 'v3DonutChart', 'v3BarChart'].includes(rowModel.controlType)">
                <v-col>
                  <v-text-field
                    label="Margin Top (px)"
                    type="number"
                    v-model="rowModel.marginTop"
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    label="Margin Bottom (px)"
                    type="number"
                    v-model="rowModel.marginBottom"
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-select
                v-if="rowModel.controlType === 'CreateDocLinks'"
                label="Document Type"
                v-model="rowModel.documentType"
                :items="docTypes"
                item-text="tmpl_name"
                item-value="tmpl_name"
                clearable>
              </v-select>

              <v-select
                v-if="['DocumentList', 'DocumentListFull', 'TaskList', 'Chart', 'v3TaskList', 'v3TaskTable', 'v3DocList', 'v3DocListFull'].includes(rowModel.controlType)"
                label="Document Type"
                v-model="rowModel.docTypes"
                clearable
                multiple
                :items="docTypes"
                item-text="tmpl_name"
                item-value="tmpl_name"
              >
              </v-select>

              <!-- New-portal (v3) Task List — max items to show before "+n more". -->
              <v-text-field
                v-if="rowModel.controlType === 'v3TaskList'"
                label="Max items"
                type="number"
                v-model.number="rowModel.limit"
                placeholder="6"
              ></v-text-field>

              <!-- New-portal (v3) Delegated — rows before "View all". -->
              <v-text-field
                v-if="rowModel.controlType === 'v3Delegated'"
                label="Rows before 'View all'"
                type="number"
                v-model.number="rowModel.limit"
                placeholder="6"
              ></v-text-field>

              <!-- New-portal (v3) PriorityStack — cards shown. -->
              <v-text-field
                v-if="rowModel.controlType === 'v3PriorityStack'"
                label="Cards shown"
                type="number"
                v-model.number="rowModel.limit"
                placeholder="3"
              ></v-text-field>

              <!-- New-portal (v3) AdminPriority / AttentionQueue — both backed by
                   the review queue; rows/cards before show-all + scope. -->
              <template v-if="['v3AdminPriority', 'v3AttentionQueue'].includes(rowModel.controlType)">
                <v-text-field
                  :label="rowModel.controlType === 'v3AdminPriority' ? 'Priorities shown' : 'Rows before \'Show all\''"
                  type="number"
                  v-model.number="rowModel.limit"
                  :placeholder="rowModel.controlType === 'v3AdminPriority' ? '3' : '5'"
                ></v-text-field>
                <v-select
                  label="Scope"
                  v-model="rowModel.scope"
                  :items="[{ text: 'All submissions in scope', value: 'all' }, { text: 'Only my evaluations', value: 'mine' }]"
                  item-text="text"
                  item-value="value"
                ></v-select>
              </template>


              <!-- New-portal (v3) Audit readiness — ready % + gap rows. -->
              <template v-if="rowModel.controlType === 'v3Audit'">
                <v-row>
                  <v-col cols="7"><v-text-field label="Subtitle" v-model="rowModel.subtitle" placeholder="EU Pay Transparency"></v-text-field></v-col>
                  <v-col cols="5">
                    <v-select
                      label="Data Source"
                      v-model="rowModel.dataSource"
                      :items="[{ text: 'Live', value: 'live' }, { text: 'Static (authored)', value: 'static' }]"
                      item-text="text"
                      item-value="value"
                    ></v-select>
                  </v-col>
                </v-row>
                <template v-if="rowModel.dataSource === 'static'">
                  <v-text-field label="Ready %" type="number" v-model.number="rowModel.ready" placeholder="84"></v-text-field>
                  <div class="d-flex align-center mt-2 mb-1">
                    <h3 class="subtitle-2">Gaps</h3>
                    <v-spacer></v-spacer>
                    <v-btn small text color="primary" @click="addV3Gap(rowModel)"><v-icon left small>add</v-icon>Add gap</v-btn>
                  </div>
                  <v-card v-for="(g, gi) in (rowModel.gaps || [])" :key="'v3gap' + gi" outlined class="mb-2 pa-2">
                    <v-row dense align="center">
                      <v-col cols="6"><v-text-field label="Label" v-model="g.label" dense hide-details></v-text-field></v-col>
                      <v-col cols="3"><v-text-field label="Count" type="number" v-model.number="g.count" dense hide-details></v-text-field></v-col>
                      <v-col cols="2"><v-text-field label="Icon" v-model="g.icon" placeholder="notes" dense hide-details></v-text-field></v-col>
                      <v-col cols="1" class="d-flex justify-end align-center"><v-btn icon small @click="removeV3Gap(rowModel, gi)"><v-icon small>delete</v-icon></v-btn></v-col>
                    </v-row>
                  </v-card>
                </template>
                <v-text-field label="Link text" v-model="rowModel.linkText" placeholder="Open audit log"></v-text-field>
                <v-combobox label="Link destination" v-model="rowModel.linkUrl" :items="internalRoutes" item-text="label" item-value="path" :return-object="false" clearable></v-combobox>
              </template>

              <!-- New-portal (v3) Calibration — rows of roles to review in context. -->
              <v-text-field
                v-if="rowModel.controlType === 'v3Calibration'"
                label="Max rows"
                type="number"
                v-model.number="rowModel.limit"
                placeholder="8"
              ></v-text-field>

              <!-- New-portal (v3) DocList / DocListFull — Recent jobs / table
                   (doc-type filter uses the shared Document Type selector below). -->
              <v-text-field
                v-if="['v3DocList', 'v3DocListFull'].includes(rowModel.controlType)"
                label="Max rows"
                type="number"
                v-model.number="rowModel.limit"
                :placeholder="rowModel.controlType === 'v3DocListFull' ? '12' : '8'"
              ></v-text-field>

              <!-- New-portal (v3) Documents table — optional status filter
                   (lifecycle status names; blank = all). Doc-type filter uses the
                   shared Document Type selector above. -->
              <v-select
                v-if="rowModel.controlType === 'v3DocListFull'"
                label="Statuses (blank = all)"
                v-model="rowModel.statuses"
                :items="v3StatusItems"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              ></v-select>

              <!-- New-portal (v3) Score Ring — a 0–100 quality ring. -->
              <template v-if="rowModel.controlType === 'v3ScoreRing'">
                <v-text-field label="Heading" v-model="rowModel.label" placeholder="Quality"></v-text-field>
                <v-row>
                  <v-col cols="6"><v-text-field label="Score" type="number" v-model.number="rowModel.score"></v-text-field></v-col>
                  <v-col cols="6"><v-text-field label="Out of" type="number" v-model.number="rowModel.max" placeholder="100"></v-text-field></v-col>
                </v-row>
                <v-text-field label="Caption" v-model="rowModel.caption"></v-text-field>
              </template>

              <!-- New-portal (v3) LinkList ("Go to" list) — heading + link rows. -->
              <template v-if="rowModel.controlType === 'v3LinkList'">
                <v-text-field label="Heading" v-model="rowModel.heading" placeholder="Go to"></v-text-field>
                <div class="d-flex align-center mt-3 mb-1">
                  <h3 class="subtitle-2">Links</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="addV3Link(rowModel)"><v-icon left small>add</v-icon>Add link</v-btn>
                </div>
                <v-card v-for="(l, li) in (rowModel.links || [])" :key="'v3link' + li" outlined class="mb-2 pa-2">
                  <v-row dense align="center">
                    <v-col cols="4"><v-text-field label="Label" v-model="l.label" dense hide-details></v-text-field></v-col>
                    <v-col cols="3"><v-text-field label="Icon" v-model="l.icon" placeholder="chevron_right" dense hide-details></v-text-field></v-col>
                    <v-col cols="4">
                      <v-combobox label="Destination" v-model="l.destination" :items="internalRoutes" item-text="label" item-value="path" :return-object="false" placeholder="route or URL" dense hide-details></v-combobox>
                    </v-col>
                    <v-col cols="1" class="d-flex justify-end">
                      <v-btn icon small @click="removeV3Link(rowModel, li)"><v-icon small>delete</v-icon></v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- New-portal (v3) FeatureTiles — large promo cards. -->
              <template v-if="rowModel.controlType === 'v3FeatureTiles'">
                <div class="d-flex align-center mt-3 mb-1">
                  <h3 class="subtitle-2">Cards</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="addV3FeatTile(rowModel)"><v-icon left small>add</v-icon>Add card</v-btn>
                </div>
                <v-card v-for="(t, ti) in (rowModel.tiles || [])" :key="'v3feat' + ti" outlined class="mb-2 pa-2">
                  <v-row dense>
                    <v-col cols="6"><v-text-field label="Title" v-model="t.title" dense hide-details></v-text-field></v-col>
                    <v-col cols="3"><v-text-field label="Icon" v-model="t.icon" placeholder="leaderboard" dense hide-details></v-text-field></v-col>
                    <v-col cols="3">
                      <v-select label="Tone" v-model="t.tone" :items="[{ text: 'Accent', value: 'accent' }, { text: 'Indigo', value: 'indigo' }]" item-text="text" item-value="value" dense hide-details></v-select>
                    </v-col>
                    <v-col cols="12"><v-text-field label="Description" v-model="t.desc" dense hide-details></v-text-field></v-col>
                    <v-col cols="4"><v-text-field label="Button label" v-model="t.cta" dense hide-details></v-text-field></v-col>
                    <v-col cols="7">
                      <v-combobox label="Button action" v-model="t.destination" :items="ctaActionItems" item-text="label" item-value="path" :return-object="false" placeholder="create / route / URL" dense hide-details></v-combobox>
                    </v-col>
                    <v-col cols="1" class="d-flex justify-end align-center">
                      <v-btn icon small @click="removeV3FeatTile(rowModel, ti)"><v-icon small>delete</v-icon></v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- New-portal (v3) ActionPanel — heading (Title above) + body + actions,
                   rendered as a button row or as icon tiles (the "Start something" card). -->
              <template v-if="rowModel.controlType === 'v3ActionPanel'">
                <v-textarea label="Body text" v-model="rowModel.body" rows="2" auto-grow></v-textarea>
                <v-select
                  label="Layout"
                  v-model="rowModel.layout"
                  :items="[{ text: 'Buttons (compact row)', value: 'buttons' }, { text: 'Tiles (icon + detail line — the \'Start something\' card)', value: 'tiles' }]"
                  item-text="text"
                  item-value="value"
                ></v-select>
                <div class="d-flex align-center mt-2 mb-1">
                  <h3 class="subtitle-2">Actions</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="addV3Action(rowModel)"><v-icon left small>add</v-icon>Add action</v-btn>
                </div>
                <v-card v-for="(a, ai) in (rowModel.actions || [])" :key="'v3act' + ai" outlined class="mb-2 pa-2">
                  <v-row dense align="center">
                    <v-col cols="5"><v-text-field label="Label" v-model="a.label" dense hide-details></v-text-field></v-col>
                    <v-col cols="6">
                      <v-combobox label="Action" v-model="a.destination" :items="ctaActionItems" item-text="label" item-value="path" :return-object="false" placeholder="create / route / URL" dense hide-details></v-combobox>
                    </v-col>
                    <v-col cols="1" class="d-flex justify-end align-center">
                      <v-btn icon small @click="removeV3Action(rowModel, ai)"><v-icon small>delete</v-icon></v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-if="rowModel.layout === 'tiles'" dense align="center">
                    <v-col cols="4">
                      <v-text-field label="Icon" v-model="a.icon" placeholder="rule" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field label="Detail line" v-model="a.sub" placeholder="e.g. Guided" dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-select label="Colour" v-model="a.tone" :items="v3TileTones" item-text="text" item-value="value" placeholder="Auto" clearable dense hide-details></v-select>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- New-portal (v3) Stepper — journey steps + current index. -->
              <template v-if="rowModel.controlType === 'v3Stepper'">
                <v-combobox
                  label="Steps"
                  v-model="rowModel.steps"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hint="Type a label and press Enter for each step"
                  persistent-hint
                ></v-combobox>
                <v-text-field label="Current step (0-based)" type="number" v-model.number="rowModel.current" placeholder="2"></v-text-field>
              </template>

              <!-- New-portal (v3) CoachToast — a dismissible coaching tip. -->
              <template v-if="rowModel.controlType === 'v3CoachToast'">
                <v-text-field label="Icon (Material Symbols name)" v-model="rowModel.icon" placeholder="mail"></v-text-field>
                <v-textarea label="Message" v-model="rowModel.message" rows="2" auto-grow></v-textarea>
              </template>

              <!-- New-portal (v3) Activity — event-stream window + scope. -->
              <template v-if="rowModel.controlType === 'v3Activity'">
                <v-row>
                  <v-col cols="6">
                    <v-text-field label="Rows before 'Show all'" type="number" v-model.number="rowModel.limit" placeholder="8"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field label="Window (days)" type="number" v-model.number="rowModel.days" placeholder="30"></v-text-field>
                  </v-col>
                </v-row>
                <v-select
                  label="Scope"
                  v-model="rowModel.scope"
                  :items="[{ text: 'Everyone in scope', value: 'all' }, { text: 'Only my activity', value: 'mine' }]"
                  item-text="text"
                  item-value="value"
                ></v-select>
              </template>

              <!-- New-portal (v3) Anomalies & Insights — governance rollup rows. -->
              <v-text-field
                v-if="rowModel.controlType === 'v3Insights'"
                label="Rows"
                type="number"
                v-model.number="rowModel.limit"
                placeholder="6"
                hint="Insight rows to show (function skew first, then org-wide rollups)"
                persistent-hint
              ></v-text-field>

              <!-- New-portal (v3) Task Table — two switches pick the view:
                   `manager` = WHICH SIDE of the work (my to-do list vs the review pipeline);
                   `scope`   = WHOSE ROWS within the review pipeline (mine vs all). -->
              <v-switch
                v-if="rowModel.controlType === 'v3TaskTable'"
                label="Manager mode — show the user's own to-do list"
                v-model="rowModel.manager"
                hint="Controls WHICH SIDE OF THE WORK the table shows. ON: work assigned TO this user to do (their workflow tasks — evaluations, document reviews, re-classifications, including not-yet-started ones), with tabs Active / Open / In progress / Overdue / Sent back / Completed, columns Due · Document · Status, and actions Open / Continue / Revise / View. OFF: the review pipeline — evaluations that have been SUBMITTED and are moving through review, with tabs All / Awaiting review / Needs approval / Revised / Flagged / Approved and the Evaluated-by + Reference columns."
                persistent-hint
                class="mb-4"
              ></v-switch>
              <v-switch
                v-if="rowModel.controlType === 'v3TaskTable' && !rowModel.manager"
                label="Only this user's own submissions"
                v-model="rowModel.scope"
                true-value="mine"
                false-value="all"
                hint="Controls WHOSE ROWS appear in the review pipeline (review mode only). ON: just the evaluations this user submitted themselves — for watching their own work move through review. OFF: every submission the user is allowed to see (the admin/reviewer view). Hidden in Manager mode because tasks are always the user's own."
                persistent-hint
                class="mb-4"
              ></v-switch>
              <v-text-field
                v-if="rowModel.controlType === 'v3TaskTable'"
                label="Rows before 'Show all'"
                type="number"
                v-model.number="rowModel.limit"
                placeholder="8"
              ></v-text-field>

              <v-select
                v-if="['DocumentListFull'].includes(rowModel.controlType)"
                label="Sort By"
                v-model="rowModel.sortBy"
                clearable
                :items="docListColumns"
                item-text="text"
                item-value="value"
              >
              </v-select>

              <v-select
                v-if="['DocumentList', 'DocumentListFull', 'TaskList', 'Chart'].includes(rowModel.controlType)"
                label="Sort Direction"
                v-model="rowModel.sortDirection"
                clearable
                :items="[{ text: 'Descending', value: 'desc' }, { text: 'Ascending', value: 'asc' }]"
                item-text="text"
                item-value="value"
              >
              </v-select>

              <v-select
                v-if="rowModel.controlType === 'DocumentList'"
                label="Lifecycles"
                v-model="rowModel.lifecycles"
                :items="rowLifecycles"
                clearable
                item-text="lifecycle_name"
                item-value="lifecycle_name"
                multiple
              >
              </v-select>

              <v-select
                v-if="rowModel.controlType === 'DocumentList'"
                label="Included Statuses"
                v-model="rowModel.statuses"
                :items="rowStatuses"
                clearable
                item-text="lifecycle_status_name"
                item-value="lifecycle_status_name"
                multiple
              >
              </v-select>

              <v-select
                v-if="rowModel.controlType === 'DocumentList'"
                label="Excluded Statuses"
                v-model="rowModel.statusesExcluded"
                :items="rowStatuses"
                clearable
                item-text="lifecycle_status_name"
                item-value="lifecycle_status_name"
                multiple
              >
              </v-select>

              <template v-if="rowModel.controlType === 'DocumentList'">
                <v-switch
                  label="Show Counts"
                  v-model="rowModel.showCounts"
                ></v-switch>
                <v-switch
                  label="Show Pie Chart"
                  v-model="rowModel.showPieChart"
                ></v-switch>
                <v-switch
                  label="Show DocType Filter"
                  v-model="rowModel.showDocTypeFilter"
                ></v-switch>
                <v-switch
                  label="Show Hierarchy Simple Filters"
                  v-model="rowModel.showHierarchyFilters"
                ></v-switch>
                <v-switch
                  label="Show Hierarchy Filter Panel"
                  v-model="rowModel.showHierarchyFilterPanel"
                ></v-switch>
                <v-switch
                  label="Show Review Groups Filter"
                  v-model="rowModel.showReviewGroupsFilter"
                ></v-switch>
                <v-switch
                  label="Show People Filter"
                  v-model="rowModel.showManagerFilter"
                ></v-switch>
                <v-switch
                  label="Show Date Filter"
                  v-model="rowModel.showDateFilter"
                ></v-switch>
                <v-switch
                  label="Show Time In Status Filter"
                  v-model="rowModel.showTimeInStatusFilter"
                ></v-switch>
                <v-switch
                  label="Show Evaluation Status"
                  v-model="rowModel.showEvaluationStatus"
                ></v-switch>
                <v-switch
                  label="Show Evaluation Counts"
                  v-model="rowModel.showEvaluationCounts"
                ></v-switch>
                <v-switch
                  label="Show Evaluation Pie Chart"
                  v-model="rowModel.showEvaluationPieChart"
                ></v-switch>
                <v-switch
                  label="Show Evaluation Score"
                  v-model="rowModel.showEvaluationScore"
                ></v-switch>
              </template>

              <v-select
                v-if="rowModel.controlType === 'TaskList'"
                label="Default Visible Columns"
                v-model="rowModel.taskListColumns"
                multiple
                :items="utils.taskListFields"
                item-text="text"
                item-value="value"
                small-chips>
              </v-select>

              <template v-if="rowModel.controlType === 'DonutChart'">
                <v-row>
                  <v-col cols="4">
                    <v-text-field label="Centre Label" v-model="rowModel.centreLabel" placeholder="e.g. Jobs"></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field label="Centre Value" v-model="rowModel.centreValue" placeholder="Auto (sum)"></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-select label="Data Source" v-model="rowModel.dataSource" :items="dataSources" item-text="text" item-value="value"></v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field label="Link Text" v-model="rowModel.linkText"></v-text-field>
                  </v-col>
                  <v-col>
                    <v-combobox label="Link URL" v-model="rowModel.linkUrl" :items="internalRoutes" item-text="label" item-value="path" :return-object="false"></v-combobox>
                  </v-col>
                </v-row>
                <template v-if="rowModel.dataSource === 'api'">
                  <v-row>
                    <v-col cols="3">
                      <v-select label="Method" v-model="rowModel.apiMethod" :items="['GET', 'POST']"></v-select>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field label="Endpoint" v-model="rowModel.apiEndpoint" placeholder="report/evaluationProgress"></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field label="Colour Field" v-model="rowModel.segmentMapping.colourField" placeholder="Optional"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field label="Label Field" v-model="rowModel.segmentMapping.labelField"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field label="Value Field" v-model="rowModel.segmentMapping.valueField"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th style="width: 140px;">Field</th>
                        <th style="width: 120px;">Condition</th>
                        <th>Values</th>
                        <th style="width: 48px;">
                          <v-btn icon @click="addDonutApiFilter">
                            <v-icon>add_circle_outline</v-icon>
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody v-if="rowModel.apiFilters">
                      <tr v-for="(f, fi) in rowModel.apiFilters" :key="'daf' + fi">
                        <td>
                          <v-select
                            v-model="f.field"
                            :items="[
                              { text: 'Doc Type', value: 'docType' },
                              { text: 'Status', value: 'status' },
                            ]"
                            hide-details
                            dense
                          ></v-select>
                        </td>
                        <td>
                          <v-select
                            v-model="f.condition"
                            :items="['include', 'exclude']"
                            hide-details
                            dense
                          ></v-select>
                        </td>
                        <td>
                          <v-combobox
                            v-model="f.values"
                            multiple
                            small-chips
                            deletable-chips
                            hide-details
                          ></v-combobox>
                        </td>
                        <td>
                          <v-btn icon @click="rowModel.apiFilters = rowModel.apiFilters.filter(o => o !== f)">
                            <v-icon>delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </template>
              </template>

              <template v-if="rowModel.controlType === 'BarChart' || rowModel.controlType === 'v3BarChart'">
                <v-select
                  v-if="rowModel.controlType === 'v3BarChart'"
                  label="Endpoint preset"
                  :value="rowModel.preset || ''"
                  :items="[{ text: '— Choose a preset (optional) —', value: '' }, ...barPresets.map((p) => ({ text: p.label, value: p.id }))]"
                  item-text="text"
                  item-value="value"
                  hint="Stamps the full wiring for a shipped Report/* endpoint (endpoint, method, field mappings, cascading filters, labels) in one click. Fine-tune below afterwards."
                  persistent-hint
                  class="mb-2"
                  @change="applyV3Preset(rowModel, barPresets, $event)"
                ></v-select>
                <v-text-field
                  v-if="rowModel.controlType === 'v3BarChart'"
                  label="Total unit label"
                  v-model="rowModel.unitLabel"
                  placeholder="e.g. employees"
                  hint="Noun after the header total ('164 employees'). Blank = bare number."
                  persistent-hint
                ></v-text-field>
                <v-row>
                  <v-col cols="3">
                    <v-text-field label="Category Label" v-model="rowModel.categoryLabel" placeholder="CATEGORY"></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field label="Value Label" v-model="rowModel.valueLabel" placeholder="VALUE"></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-combobox label="Bar Colour" v-model="rowModel.barColor" :items="hexColours"></v-combobox>
                  </v-col>
                  <v-col cols="3">
                    <v-select label="Data Source" v-model="rowModel.dataSource" :items="dataSources" item-text="text" item-value="value"></v-select>
                  </v-col>
                </v-row>
                <template v-if="rowModel.dataSource === 'api'">
                  <v-row>
                    <v-col cols="3">
                      <v-select label="Method" v-model="rowModel.apiMethod" :items="['GET', 'POST']"></v-select>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field label="Endpoint" v-model="rowModel.apiEndpoint" placeholder="report/headcountByLevel"></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-text-field label="Label Field" v-model="rowModel.rowMapping.labelField"></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-text-field label="Value Field" v-model="rowModel.rowMapping.valueField"></v-text-field>
                    </v-col>
                  </v-row>
                </template>
              </template>
            </v-col>
            <v-col cols="8" v-if="rowModel.controlType === 'DocumentList' || rowModel.controlType === 'DocumentListFull'">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th></th>
                    <th>Label</th>
                    <th>Field</th>
                    <th>Visible</th>
                    <th>
                      <v-btn
                        icon
                        @click="
                          rowModel.docListColumns.push({
                            text: '',
                            value: '',
                            visible: false,
                          })
                        "
                        ><v-icon>add_circle_outline</v-icon></v-btn
                      >
                    </th>
                  </tr>
                </thead>
                <draggable
                  v-model="rowModel.docListColumns"
                  handle=".draghandle"
                  tag="tbody"
                >
                  <tr
                    v-for="(c, ci) in rowModel.docListColumns"
                    :key="'dlCol' + ci"
                  >
                    <td>
                      <v-icon class="draghandle">drag_indicator</v-icon>
                    </td>
                    <td>
                      <v-text-field
                        v-model="c.text"
                        hide-details
                      ></v-text-field>
                    </td>
                    <td>
                      <v-select
                        :items="documentListFields"
                        item-text="text"
                        item-value="value"
                        v-model="c.value"
                        @change="onDocListFieldSelect(c)"
                        hide-details
                      ></v-select>
                    </td>
                    <td>
                      <v-switch v-model="c.visible" hide-details></v-switch>
                    </td>
                    <td>
                      <v-btn
                        icon
                        @click="
                          rowModel.docListColumns =
                            rowModel.docListColumns.filter((o) => o !== c)
                        "
                        ><v-icon>delete</v-icon></v-btn
                      >
                    </td>
                  </tr>
                </draggable>
              </v-simple-table>
            </v-col>
            <v-col cols="8" v-if="rowModel.controlType === 'Chart'">
              <v-row v-if="rowModel.controlType === 'Chart'">
                <v-col>
                  <v-select
                    :items="chartTypes"
                    v-model="rowModel.chartType"
                    label="Chart Type"
                    item-text="text"
                    item-value="value"
                    clearable
                  ></v-select>
                  <v-select
                    :items="tagTypes"
                    v-model="rowModel.tagTypeName"
                    label="Tag Type"
                    item-text="tag_type_name"
                    item-value="tag_type_name"
                    clearable
                  ></v-select>
                  <v-select
                    :items="chartDisplayTypes"
                    v-model="rowModel.chartDisplayType"
                    label="Display Type"
                    item-text="text"
                    item-value="value"
                    clearable
                  ></v-select>
                <v-switch
                  label="Hierarchy Type Filters Optional"
                  v-model="rowModel.chartFilterOptional"
                ></v-switch>
                </v-col>
                <v-col cols="6" v-if="rowModel.chartType === 'tagDistribution'">
                  <v-select
                    label="Classifer Type Filters"
                    placeholder="< All >"
                    v-model="rowModel.chartClassifierFilters"
                    :items="tagClassifierTypes"
                    multiple
                    item-text="name"
                    item-value="tag_classifier_type_id"
                  ></v-select>
                  <v-autocomplete
                    :items="tagValues"
                    v-model="rowModel.initialTagValueId"
                    label="Initial Tag Value"
                    item-text="value"
                    item-value="tag_value_id"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="6" v-if="rowModel.chartType === 'jobTagClassifiers'">
                  <v-select
                    label="Classifer Type"
                    v-model="rowModel.chartClassifierTypeId"
                    :items="tagClassifierTypes"
                    item-text="name"
                    item-value="tag_classifier_type_id"
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="8" v-if="rowModel.controlType === 'DonutChart'">
              <template v-if="rowModel.dataSource !== 'api'">
                <h3 class="mb-2">Segments</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Label</th>
                      <th style="width: 100px;">Value</th>
                      <th style="width: 140px;">Colour</th>
                      <th style="width: 48px;">
                        <v-btn icon @click="addDonutSegment">
                          <v-icon>add_circle_outline</v-icon>
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                  <draggable
                    v-if="rowModel.segments"
                    v-model="rowModel.segments"
                    handle=".draghandle"
                    tag="tbody"
                  >
                    <tr v-for="(s, si) in rowModel.segments" :key="'seg' + si">
                      <td><v-icon class="draghandle">drag_indicator</v-icon></td>
                      <td><v-text-field v-model="s.label" hide-details></v-text-field></td>
                      <td><v-text-field v-model.number="s.value" type="number" hide-details></v-text-field></td>
                      <td>
                        <v-combobox
                          v-model="s.color"
                          :items="hexColours"
                          hide-details
                          dense
                        >
                          <template v-slot:prepend>
                            <v-icon :color="s.color">circle</v-icon>
                          </template>
                        </v-combobox>
                      </td>
                      <td>
                        <v-btn icon @click="rowModel.segments = rowModel.segments.filter(o => o !== s)">
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </draggable>
                </v-simple-table>
              </template>

              <template v-else>
                <h3 class="mb-2">API Parameters</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Value</th>
                      <th style="width: 48px;">
                        <v-btn icon @click="addApiParam(rowModel)">
                          <v-icon>add_circle_outline</v-icon>
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                  <tbody v-if="rowModel.apiParams">
                    <tr v-for="(p, pi) in rowModel.apiParams" :key="'dp' + pi">
                      <td><v-text-field v-model="p.name" hide-details></v-text-field></td>
                      <td><v-text-field v-model="p.value" hide-details></v-text-field></td>
                      <td>
                        <v-btn icon @click="rowModel.apiParams = rowModel.apiParams.filter(o => o !== p)">
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <h3 class="mb-2 mt-4">Colour Overrides</h3>
                <div class="caption text--secondary mb-2">
                  Optional — map a label returned by the API to a specific colour.
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th style="width: 160px;">Colour</th>
                      <th style="width: 48px;">
                        <v-btn icon @click="addDonutColourOverride">
                          <v-icon>add_circle_outline</v-icon>
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                  <tbody v-if="rowModel.segmentColours">
                    <tr v-for="(c, ci) in rowModel.segmentColours" :key="'sc' + ci">
                      <td><v-text-field v-model="c.label" hide-details></v-text-field></td>
                      <td>
                        <v-combobox
                          v-model="c.colour"
                          :items="hexColours"
                          hide-details
                          dense
                        >
                          <template v-slot:prepend>
                            <v-icon :color="c.colour">circle</v-icon>
                          </template>
                        </v-combobox>
                      </td>
                      <td>
                        <v-btn icon @click="rowModel.segmentColours = rowModel.segmentColours.filter(o => o !== c)">
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </template>
            </v-col>

            <v-col cols="8" v-if="rowModel.controlType === 'BarChart' || rowModel.controlType === 'v3BarChart'">
              <template v-if="rowModel.dataSource !== 'api'">
                <h3 class="mb-2">Rows</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Label</th>
                      <th style="width: 120px;">Value</th>
                      <th style="width: 48px;">
                        <v-btn icon @click="addBarRow">
                          <v-icon>add_circle_outline</v-icon>
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                  <draggable
                    v-if="rowModel.rows"
                    v-model="rowModel.rows"
                    handle=".draghandle"
                    tag="tbody"
                  >
                    <tr v-for="(r, ri) in rowModel.rows" :key="'barrow' + ri">
                      <td><v-icon class="draghandle">drag_indicator</v-icon></td>
                      <td><v-text-field v-model="r.label" hide-details></v-text-field></td>
                      <td><v-text-field v-model.number="r.value" type="number" hide-details></v-text-field></td>
                      <td>
                        <v-btn icon @click="rowModel.rows = rowModel.rows.filter(o => o !== r)">
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </draggable>
                </v-simple-table>
              </template>

              <template v-else>
                <h3 class="mb-2">API Parameters</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Value</th>
                      <th style="width: 48px;">
                        <v-btn icon @click="addApiParam(rowModel)">
                          <v-icon>add_circle_outline</v-icon>
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                  <tbody v-if="rowModel.apiParams">
                    <tr v-for="(p, pi) in rowModel.apiParams" :key="'bp' + pi">
                      <td><v-text-field v-model="p.name" hide-details></v-text-field></td>
                      <td><v-text-field v-model="p.value" hide-details></v-text-field></td>
                      <td>
                        <v-btn icon @click="rowModel.apiParams = rowModel.apiParams.filter(o => o !== p)">
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </template>

              <h3 class="mb-2 mt-4">Filters</h3>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th style="min-width: 90px;">Key</th>
                    <th style="min-width: 90px;">Label</th>
                    <th style="width: 60px;">Multi</th>
                    <th style="min-width: 110px;">All Items Field</th>
                    <th style="width: 48px;">
                      <v-btn icon @click="addBarFilter">
                        <v-icon>add_circle_outline</v-icon>
                      </v-btn>
                    </th>
                  </tr>
                </thead>
                <tbody v-if="rowModel.filters">
                  <tr v-for="(f, fi) in rowModel.filters" :key="'bf' + fi">
                    <td><v-text-field v-model="f.key" hide-details></v-text-field></td>
                    <td><v-text-field v-model="f.label" hide-details></v-text-field></td>
                    <td class="text-center">
                      <v-checkbox v-model="f.multiple" hide-details class="mt-0 pt-0"></v-checkbox>
                    </td>
                    <td>
                      <v-text-field
                        v-model="f.allItemsField"
                        hide-details
                        placeholder="e.g. levels"
                        clearable
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn icon @click="rowModel.filters = rowModel.filters.filter(o => o !== f)">
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showTileEdit" max-width="1000px">
      <v-card v-if="tileModel">
        <v-card-title>
          Tile Settings
          <v-spacer />
          <v-btn icon @click="showTileEdit = false"
            ><v-icon>close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-select
                v-if="rowModel.controlType.startsWith('LinkTiles') || rowModel.controlType === 'FeatureTiles'"
                label="Type"
                v-model="tileModel.type"
                :items="tileTypes"
                item-text="text"
                item-value="value"
              >
              </v-select>

              <template
                v-if="
                  rowModel.controlType === 'Metabase'
                "
              >
                <v-row>
                  <v-col>
                    <v-select
                      label="Resource Type"
                      v-model="tileModel.metabaseType"
                      :items="['question', 'dashboard']"
                    >
                    </v-select>
                  </v-col>
                  <v-col>
                    <v-text-field
                      label="Resource ID"
                      v-model="tileModel.metabaseId"
                      type="number"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-switch label="Include Title" v-model="tileModel.incTitle">
                </v-switch>
                <v-switch label="Show Border" v-model="tileModel.showBorder">
                </v-switch>
              </template>
              <template v-else-if="rowModel.controlType === 'QuickLinks'">
                <v-text-field label="Title" v-model="tileModel.title">
                </v-text-field>

                <div class="d-flex justify-space-between">
                  <v-icon
                    :class="tileModel.icon"
                    :color="tileModel.icon_colour"
                    style="width: 25px"
                    >{{
                      tileModel.icon ? tileModel.icon.split(" ")[0] : ""
                    }}</v-icon
                  >
                  <v-text-field
                    style="max-width: 120px"
                    label="Icon"
                    v-model="tileModel.icon"
                  >
                  </v-text-field>
                  <v-text-field
                    style="max-width: 80px"
                    label="Size"
                    type="number"
                    v-model="tileModel.icon_size"
                  >
                  </v-text-field>
                  <v-combobox
                    style="max-width: 100px"
                    label="Icon Colour"
                    v-model="tileModel.icon_colour"
                    :items="colours"
                  >
                  </v-combobox>
                </div>
              </template>
              <template v-else-if="rowModel.controlType === 'LinkTiles' || rowModel.controlType === 'LinkTilesAlt'">
                <v-text-field label="Title" v-model="tileModel.title">
                </v-text-field>

                <div class="d-flex justify-space-between">
                  <v-icon
                    :class="tileModel.icon"
                    :color="tileModel.icon_colour"
                    style="width: 25px"
                    >{{
                      tileModel.icon ? tileModel.icon.split(" ")[0] : ""
                    }}</v-icon
                  >
                  <v-text-field
                    style="max-width: 120px"
                    label="Icon"
                    v-model="tileModel.icon"
                  >
                  </v-text-field>
                  <v-text-field
                    style="max-width: 80px"
                    label="Size"
                    type="number"
                    v-model="tileModel.icon_size"
                  >
                  </v-text-field>
                  <v-combobox
                    style="max-width: 100px"
                    label="Icon Colour"
                    v-model="tileModel.icon_colour"
                    :items="colours"
                  >
                  </v-combobox>
                </div>
                <v-text-field label="Hero Image" v-model="tileModel.hero_image">
                </v-text-field>
                <v-row>
                  <v-col>
                    <v-text-field label="Image Width" v-model="tileModel.image_width" />
                  </v-col>
                  <v-col>
                    <v-text-field label="Image Height" v-model="tileModel.image_height" />
                  </v-col>
                </v-row>
              </template>
              <template v-else-if="rowModel.controlType === 'FeatureTiles'">
                <v-text-field label="Title" v-model="tileModel.title">
                </v-text-field>

                <v-textarea
                  label="Description"
                  v-model="tileModel.description"
                  rows="3"
                  auto-grow
                ></v-textarea>

                <v-select
                  label="Tile Colour"
                  v-model="tileModel.color"
                  :items="tileColours"
                  item-text="text"
                  item-value="value"
                ></v-select>

                <div class="d-flex justify-space-between">
                  <v-icon
                    :class="tileModel.icon"
                    :color="tileModel.iconColor || tileModel.icon_colour"
                    style="width: 25px"
                    >{{
                      tileModel.icon ? tileModel.icon.split(" ")[0] : ""
                    }}</v-icon
                  >
                  <v-text-field
                    style="max-width: 260px"
                    label="Icon"
                    v-model="tileModel.icon"
                  ></v-text-field>
                  <v-combobox
                    style="max-width: 140px"
                    label="Icon Colour"
                    v-model="tileModel.iconColor"
                    :items="hexColours"
                  ></v-combobox>
                </div>
              </template>
              <template v-else-if="rowModel.controlType === 'LinkTilesColoured'">
                <v-text-field label="Title" v-model="tileModel.title">
                </v-text-field>

                <v-text-field label="Link Text" v-model="tileModel.link_text">
                </v-text-field>

                <v-combobox
                  label="Colour"
                  v-model="tileModel.icon_colour"
                  :items="colours"
                >
                </v-combobox>

                <div class="d-flex justify-space-between">
                  <v-icon
                    :class="tileModel.icon"
                    :color="tileModel.icon_colour"
                    style="width: 25px"
                    >{{
                      tileModel.icon ? tileModel.icon.split(" ")[0] : ""
                    }}</v-icon
                  >
                  <v-text-field
                    style="max-width: 260px"
                    label="Icon"
                    v-model="tileModel.icon"
                  >
                  </v-text-field>
                  <v-text-field
                    style="max-width: 80px"
                    label="Size"
                    type="number"
                    v-model="tileModel.icon_size"
                  >
                  </v-text-field>
                </div>
              </template>
              <template v-else>
                <v-text-field label="Title" v-model="tileModel.title">
                </v-text-field>

                <v-select
                  label="Colour"
                  v-model="tileModel.icon_colour"
                  :items="colours"
                >
                </v-select>
              </template>
            </v-col>
            <v-col cols="6">
              <v-row>
                <v-col>
                  <v-text-field
                    label="Tile Columns"
                    type="number"
                    v-model="tileModel.tile_cols"
                  >
                  </v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    v-if="rowModel.controlType === 'Metabase' || rowModel.controlType === 'LinkTiles' || rowModel.controlType === 'LinkTilesAlt' || rowModel.controlType === 'FeatureTiles'"
                    label="Height"
                    type="text"
                    v-model="tileModel.height"
                    hint="px if number (e.g. 180), otherwise any CSS length"
                    persistent-hint
                  >
                  </v-text-field>
                </v-col>
              </v-row>

              <template v-if="rowModel.controlType === 'Metabase'">
                <h3>Parameters</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Value</th>
                      <th>
                        <v-btn
                          icon
                          @click="
                            if (!tileModel.metabaseParams)
                              $set(tileModel, 'metabaseParams', []);

                            tileModel.metabaseParams.push({
                              name: '',
                              value: '',
                            });
                          "
                          ><v-icon>add_circle_outline</v-icon></v-btn
                        >
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(c, ci) in tileModel.metabaseParams"
                      :key="'p' + ci"
                    >
                      <td>
                        <v-combobox
                          :items="metabaseParams"
                          v-model="c.name"
                          hide-details
                        ></v-combobox>
                      </td>
                      <td>
                        <div
                          v-if="metabaseParams.includes(c.name)"
                          class="caption text--disabled mt-4"
                        >
                          <span>auto-set</span>
                        </div>
                        <v-text-field
                          v-else
                          v-model="c.value"
                          hide-details
                        ></v-text-field>
                      </td>
                      <td>
                        <v-btn
                          icon
                          @click="
                            tileModel.metabaseParams =
                              tileModel.metabaseParams.filter((o) => o !== c)
                          "
                          ><v-icon>delete</v-icon></v-btn
                        >
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </template>
              <template v-else-if="tileModel.type === 'createdoc'">
                <v-select
                  :items="docTypes"
                  v-model="tileModel.createDocType"
                  label="Doc Type"
                  item-text="tmpl_name"
                  item-value="tmpl_name"
                  clearable
                ></v-select>
                <v-text-field
                  label="Call to Action"
                  v-model="tileModel.call_to_action"
                >
                </v-text-field>
              </template>
              <template v-else-if="tileModel.type === 'ssolink'">
                <v-text-field
                  label="URL"
                  v-model="tileModel.url"
                >
                </v-text-field>
                <v-switch
                  label="Open in new tab"
                  v-model="tileModel.open_newtab"
                >
                </v-switch>
                <v-select
                  :items="ssoTypes"
                  v-model="tileModel.sso"
                  label="SSO Type"
                ></v-select>
                <v-text-field
                  label="Call to Action"
                  v-model="tileModel.call_to_action"
                >
                </v-text-field>
              </template>
              <template v-else>
                <v-combobox
                  label="URL"
                  v-model="tileModel.url"
                  :items="internalRoutes"
                  item-text="label"
                  item-value="path"
                  :return-object="false"
                >
                </v-combobox>
                <v-text-field
                  label="Passed Params"
                  v-model="tileModel.passedParams">
                </v-text-field>
                <v-switch
                  label="Open in new tab"
                  v-model="tileModel.open_newtab"
                >
                </v-switch>
                <v-text-field
                  label="Call to Action"
                  v-model="tileModel.call_to_action"
                >
                </v-text-field>
              </template>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Loading :isVisible="isLoading" />
    <ResponseHandler :serviceResponse="response"></ResponseHandler>
    <v-file-input
      v-show="false"
      accept=".json"
      ref="inputFile"
      dense
      @change="uploadDefinitionFromFile"
      label="Select File"
    ></v-file-input>
  </v-container>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import ResponseHandler from "@/components/ResponseHandler";
import Landing from "@/components/landingPages/cLanding";
import V3Editor from "@/components/landingPages/cLandingV3Editor";
import { flatStatFields } from "@rm-ui/homepage/statFields";
import { DONUT_PRESETS, BAR_PRESETS, presetById, applyEndpointPreset } from "@rm-ui/homepage/endpointPresets";
import utils from "@/common/utils.js";
import landingUtils from "@/components/landingPages/landingUtils.js";
import draggable from "vuedraggable";

export default {
  name: "vLandingConfig",
  components: {
    ResponseHandler,
    Landing,
    V3Editor,
    draggable,
  },
  props: {},
  data: function () {
    return {
      utils: utils,
      isLoading: false,
      response: null,
      landingPages: [],
      permissionGroups: [],
      page: null,
      pageDefinition: null,
      showEditDefinition: false,
      showDefinitionEdit: false,
      isDirty: false,
      showPageEdit: false,
      showColEdit: false,
      showRowEdit: false,
      showTabEdit: false,
      tabModel: null,
      activeTab: 0,
      colModel: null,
      rowModel: null,
      showTileEdit: false,
      tileModel: null,
      ssoTypes: ["", "zendesk"],
      controlTypes: [
        { text: "Tiles", value: "LinkTiles" },
        { text: "TilesAlt", value: "LinkTilesAlt" },
        { text: "Tiles Coloured", value: "LinkTilesColoured" },
        { text: "Feature Tiles (Pastel)", value: "FeatureTiles" },
        { text: "QuickLinks", value: "QuickLinks" },
        { text: "Stepper", value: "Stepper" },
        { text: "Small Stepper", value: "StepperSmall" },
        { text: "Create Doc Links", value: "CreateDocLinks" },
        { text: "Task List", value: "TaskList" },
        { text: "Document List", value: "DocumentList" },
        { text: "Document List Full", value: "DocumentListFull" },
        { text: "Metabase Dashboard", value: "Metabase" },
        { text: "Chart", value: "Chart" },
        { text: "Donut Chart", value: "DonutChart" },
        { text: "Bar Chart", value: "BarChart" },
        // New-portal (v3) homepage widgets. Aliased with a `v3` prefix so they
        // never collide with legacy controlTypes/components. The legacy
        // renderer shows a placeholder for these; they render for real in the
        // new portal (rm-portal-beta) via its widget registry.
        { text: "Hero (new portal)", value: "v3Hero" },
        { text: "Stat Row (new portal)", value: "v3StatRow" },
        { text: "Quick Links (new portal)", value: "v3QuickLinks" },
        { text: "Donut Chart (new portal)", value: "v3DonutChart" },
        { text: "Bar Chart (new portal)", value: "v3BarChart" },
        { text: "Feedback Strip (new portal)", value: "v3FeedbackStrip" },
        { text: "My Tasks (legacy — use Task Table with Manager mode)", value: "v3TaskList" },
        { text: "Task Table (review queue / manager tasks) (new portal)", value: "v3TaskTable" },
        { text: "My Delegations (new portal)", value: "v3Delegated" },
        { text: "Empty State (new portal)", value: "v3Empty" },
        { text: "All Priorities (Admin View) (new portal)", value: "v3AdminPriority" },
        { text: "All Reviews (Admin View) (new portal)", value: "v3AttentionQueue" },
        { text: "Recent Activity (new portal)", value: "v3Activity" },
        { text: "My Priorities (new portal)", value: "v3PriorityStack" },
        { text: "Score Ring (new portal)", value: "v3ScoreRing" },
        { text: "Go To Links / List (new portal)", value: "v3LinkList" },
        { text: "Feature Cards (new portal)", value: "v3FeatureTiles" },
        { text: "Recent Jobs / Doc List (new portal)", value: "v3DocList" },
        { text: "Documents Table (new portal)", value: "v3DocListFull" },
        { text: "Action Panel (new portal)", value: "v3ActionPanel" },
        { text: "Journey Stepper (new portal)", value: "v3Stepper" },
        { text: "Coaching Tip (new portal)", value: "v3CoachToast" },
        { text: "Calibration (new portal)", value: "v3Calibration" },
        { text: "Audit Readiness (new portal)", value: "v3Audit" },
        { text: "Anomalies & Insights (new portal)", value: "v3Insights" },
      ],
      dataSources: [
        { text: "Static", value: "static" },
        { text: "API Endpoint", value: "api" },
      ],
      // Token hint for v3 donut centre/link fields (kept in data so the
      // literal {{docType}} braces aren't parsed as a template interpolation).
      docTypeTokenHint: "Use {{docType}} to insert the selected document type",
      docTypeTokenPlaceholder: "e.g. View {{docType}}",
      // New-portal (v3) FeedbackStrip status options (match the beta widget map).
      v3FbStatuses: [
        { text: "Approved", value: "approved" },
        { text: "Submitted", value: "submitted" },
        { text: "In progress", value: "in-progress" },
        { text: "Sent back", value: "returned" },
        { text: "Ready", value: "ready" },
      ],
      // New-portal (v3) tile icon tones — match the beta widget's colour set.
      // Blank = auto (cycles accent/violet/green by position).
      v3TileTones: [
        { text: "Accent (brand)", value: "accent" },
        { text: "Violet", value: "violet" },
        { text: "Green", value: "green" },
        { text: "Teal", value: "teal" },
        { text: "Amber", value: "amber" },
        { text: "Rose", value: "rose" },
        { text: "Neutral", value: "neutral" },
      ],
      // Stat-tile icon-chip tones (only those with a `.v3-ic--` class — no teal).
      v3StatTones: [
        { text: "Accent (brand)", value: "accent" },
        { text: "Green", value: "green" },
        { text: "Amber", value: "amber" },
        { text: "Rose", value: "rose" },
        { text: "Violet", value: "violet" },
        { text: "Neutral", value: "neutral" },
      ],
      tileColours: [
        { text: "Blue", value: "blue" },
        { text: "Yellow", value: "yellow" },
        { text: "Pink", value: "pink" },
        { text: "Orange", value: "orange" },
        { text: "Green", value: "green" },
        { text: "Purple", value: "purple" },
      ],
      controlStyle: [
        { text: "Static", value: "static" },
        { text: "Collapsible (default expanded)", value: "collapsible-expanded" },
        { text: "Collapsible (default collapsed)", value: "collapsible-collapsed" }
      ],
      tileTypes: [
        { value: "link", text: "Link" },
        { value: "ssolink", text: "External SSO Link" },
        { value: "createdoc", text: "Create Doc Dialog" },
      ],
      chartTypes: [
        { value: "tagDistribution", text: "Tag Distribution" },
        { value: "tagProficiencies", text: "Tag by Proficiencies" },
        { value: "jobTagClassifiers", text: "Jobs by Tag Classifiers" },
      ],
      chartDisplayTypes: [
        { value: "bars", text: "Bars" },
        { value: "pieset", text: "Pie Charts" },
      ],
      colours: ["", "red", "blue", "purple", "green", "orange", "gray"],
      hexColours: [
        "#2B9DBF",
        "#D4A400",
        "#EA3785",
        "#ED742D",
        "#2cd946",
        "#6938b9",
        "#50B3CB",
        "#FFCB00",
        "#D9D9D9",
      ],
      internalRoutes: [],
      metabaseParams: ["client_id", "client_name", "user_id", "name", "email"],
    };
  },
  mounted() {},
  created() {
    if (!this.hierarchiesLoading) this.init();
  },
  watch: {
    hierarchiesLoading(val) {
      if (!val) this.init();
    },
  },
  computed: {
    // Endpoint preset catalogues for the v3 chart widgets (shared with beta).
    donutPresets() {
      return DONUT_PRESETS;
    },
    barPresets() {
      return BAR_PRESETS;
    },
    // Hero CTA destinations = the create-dialog action + the available routes
    // (same list used for link/tile destinations).
    ctaActionItems() {
      return [{ label: "Open create dialog", path: "create" }, ...this.internalRoutes];
    },
    // Stat-field dropdown options for the StatRow tiles editor: the available
    // metrics + the tenant's doc types and lifecycle statuses.
    statFieldItems() {
      return flatStatFields({
        docTypes: (this.docTypes || []).map((d) => d.tmpl_name).filter(Boolean),
        statuses: (this.lifecycleStatuses || []).map((s) => s.lifecycle_status_name).filter(Boolean),
      });
    },
    // Distinct lifecycle status names, for the per-tile status scope multiselect.
    // Matches the `status` dimension of Report/docCounts' byTypeStatus cross-tab.
    v3StatusItems() {
      const names = (this.lifecycleStatuses || []).map((s) => s.lifecycle_status_name).filter(Boolean);
      return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    },
    v3DocTypeItems() {
      const names = (this.docTypes || []).map((d) => d.tt_name).filter(Boolean);
      return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    },
    ...mapState({
      newUi: (state) => state.theme.newUi,
      docsLoading: (state) => state.docs.loading,
      docStatusSummary: (state) => state.docs.docStatusSummary,
      allDocStatuses: (state) => state.docs.allDocStatuses,
      docTypeSummary: (state) => state.docs.docTypeSummary,
      hierarchiesLoading: (state) => state.hierarchies.loading,
      tagTypes: (state) => state.hierarchies.tagTypes,
      docTypes: (state) => state.hierarchies.docTypes,
      docListColumns: (state) => state.settings.config.docListColumns,
      lifecycleStatuses: (state) => state.hierarchies.lifecycleStatuses,
      tagClassifierTypes: (state) => state.hierarchies.tagClassifierTypes,
    }),
    tagValues() {
      return this.tagTypes?.find(tt => tt.tag_type_name === this.rowModel?.tagTypeName)?.values || [];
    },
    documentListFields() {
      return [
        ...this.docListColumns,
        { text: "Remaining Time", value: "remainingTime" },
        { text: "Time In Status Summary", value: "tisTimeToComplete" },
        ...this.allDocStatuses
          .filter(s => s.show && !s.final_status && !s.status.startsWith('Part '))
          .map(s => {
            return {
              text: `Days In ${s.status}`,
              value: `dis_${s.status}`
            }
          })
      ];
    },
    rowLifecycles() {
      if (!this.pageDefinition || !this.rowModel) return [];

      return this.docTypes
        .filter((s) => !this.rowModel.docTypes || this.rowModel.docTypes.length === 0 || this.rowModel.docTypes.some(dt => dt.toLowerCase() === s.tmpl_name.toLowerCase()))
        .reduce((p, c) => {
          return [...p, ...c.lifecycles ]
        }, []);
    },
    tabSelect() {
      const tabs = this.pageDefinition?.definition?.tabs;
      if (!tabs || tabs.length <= 1)
        return null;

      return tabs.map((t, ti) => {
        return {
          title: t.title,
          index: ti
        }
      })
    },
    rowStatuses() {
      if (!this.pageDefinition || !this.rowModel) return [];

      const lifecycleIdsIncluded = this.docTypes
        .filter((s) => !this.rowModel.docTypes || this.rowModel.docTypes.length === 0 || this.rowModel.docTypes.some(dt => dt.toLowerCase() === s.tmpl_name.toLowerCase()))
        .reduce((p, c) => {
          return [...p, ...c.lifecycles?.filter(l => !this.rowModel.lifecycles || this.rowModel.lifecycles.length === 0 || this.rowModel.lifecycles.includes(l.lifecycle_name)) ]
        }, [])
        .map(l => l.lifecycle_id);

      return this.lifecycleStatuses
        .filter(
          (s) =>
            (!this.rowModel.docTypes || this.rowModel.docTypes.length === 0 || this.rowModel.docTypes.some(dt => dt.toLowerCase() === s.tmpl_name.toLowerCase())) &&
            (!lifecycleIdsIncluded || !lifecycleIdsIncluded.length || lifecycleIdsIncluded.includes(s.lifecycle_id))
        )
        .map((s) => {
          return {
            lifecycle_status_id: s.lifecycle_status_id,
            lifecycle_status_name: s.lifecycle_status_name
          };
        });
    },
    activeTabDefinition: {
      get() {
        const definition = this.pageDefinition?.definition;
        if (!definition)
          return;

        return definition.tabs[this.activeTab];
      },
      set(newValue) {
        const definition = this.pageDefinition?.definition;
        if (!definition)
          return;

        definition.tabs[this.activeTab] = newValue;
      }
    },
    isV3Engine() {
      return this.pageDefinition?.definition?.engine === "v3";
    },
    // Control-type options for the editor UI. With the v3 builder selected, only
    // the new (v3) controls are offered, and the "(new portal)" qualifier is
    // dropped from the labels (it's redundant once you're in the v3 builder).
    // The legacy engine keeps the full list unchanged.
    uiControlTypes() {
      if (!this.isV3Engine) return this.controlTypes;
      return this.controlTypes
        .filter((c) => String(c.value).startsWith("v3") && c.value !== "v3Group")
        .map((c) => ({ ...c, text: String(c.text).replace(/\s*\(new portal\)\s*$/i, "") }));
    },
    // The v3 layout's rows live in a single full-width container column on the
    // active tab; each row is a `v3Group`. Read-only here — mutation goes
    // through the v3* methods (which ensure the container exists first).
    v3Rows() {
      const tab = this.activeTabDefinition;
      return (tab && tab.cols && tab.cols[0] && tab.cols[0].rows) || [];
    },
  },
  methods: {
    sessionExpired(err) {
      this.$emit("sessionExpired", err);
    },
    init() {
      let possibleError = false;
      this.isLoading = true;
      axios
        .get("admin/landingPageConfigLoad")
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            const data = resp.data.Data;
            const landingPages = data.pages;
            const definitionPgs = data.definitionPgs;
            const permissionGroups = data.permissionGroups;
            landingPages.forEach((p) => {
              p.definitions.forEach((d) => {
                permissionGroups.forEach((pg) => {
                  const lppg = definitionPgs.find(
                    (dpg) =>
                      dpg.lpd_id === d.lpd_id &&
                      dpg.permission_group_id === pg.permission_group_id
                  );
                  d[`pg${pg.permission_group_id}_enabled`] = !!lppg;
                });
              });
              p.menu_section = p.menu_section || 'Main';
            });
            this.landingPages = landingPages.sort((a, b) =>
              a.order > b.order ? 1 : -1
            );
            this.permissionGroups = permissionGroups;
          }
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });

      this.internalRoutes = this.$router
        .getRoutes()
        .filter((r) => r.meta?.newUi)
        .map((r) => {
          return {
            label: r.meta?.text || r.name,
            path: r.name,
          };
        });
    },
    editPage(p) {
      this.page = p;
      this.showPageEdit = true;
    },
    addPage() {
      let maxPosition = Math.max.apply(
        null,
        this.landingPages.map((p) => p.order || 0)
      );
      if (maxPosition < 0) maxPosition = 0;
      this.page = {
        lp_id: null,
        menu_section: "Main",
        page_route: "",
        active: true,
        title: "",
        icon: "",
        hide_header_create: false,
        definitions: [],
        order: ++maxPosition,
      };
      this.showPageEdit = true;
    },
    openDefinitionForEdit(p, pd) {
      this.editDefinition(p, pd);
      this.showEditDefinition = true;
    },
    editDefinition(p, pd) {
      this.page = p;

      this.pageDefinition = {
        lp_id: pd.lp_id,
        lpd_id: pd.lpd_id,
        pageTitle: p.title,
        description: pd.description,
        active: pd.active,
        definition: this.prepDefinition(pd.definition),
      };
    },
    prepDefinition(definition) {
      definition = definition ? JSON.parse(definition) : {};
      landingUtils.prepLandingPageDefinition(definition);

      return definition;
    },
    addDefinition(p, pd) {
      let newDefinition;
      if (pd) {
        newDefinition = JSON.parse(JSON.stringify(pd));
        const oldDesc = pd.description || "New Definition";
        let i = 1,
          newDesc = oldDesc;
        while (p.definitions.some((d) => d.description === newDesc)) {
          i++;
          newDesc = `${oldDesc} (${i})`;
        }
        newDefinition.description = newDesc;
      } else {
        const newDesc = prompt("Enter Definition Description", "");
        if (!newDesc) return;
        newDefinition = {
          lp_id: p.lp_id,
          description: newDesc,
          active: true,
        };
      }
      this.permissionGroups.forEach((pg) => {
        newDefinition[`pg${pg.permission_group_id}_enabled`] = false;
      });
      let possibleError = false;
      this.isLoading = true;
      axios
        .post("admin/landingPageDefinitionSave", {
          lp_id: p.lp_id,
          description: newDefinition.description,
          definition: newDefinition.definition,
          active: newDefinition.active,
        })
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            newDefinition.lpd_id = resp.data.Data;
            p.definitions.push(newDefinition);
          }
          this.response = resp.data;
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    },
    deleteDefinition(p, pd) {
      if (confirm("Delete definition?")) {
        let possibleError = false;
        this.isLoading = true;
        axios
          .post("admin/landingPageDefinitionDelete", {
            lpd_id: pd.lpd_id,
          })
          .then((resp) => {
            possibleError = true;
            if (resp.data.Status === "OK") {
              p.definitions = p.definitions.filter(
                (d) => d.lpd_id !== pd.lpd_id
              );
            }
            this.response = resp.data;
            this.isLoading = false;
          })
          .catch((err) => {
            this.isLoading = false;
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      }
    },
    closeDefinition() {
      // const definitionJson = JSON.stringify(this.pageDefinition.definition);
      // const origDefinition = this.page
      //   ? this.page.definitions.find(
      //       (p) => p.lpd_id === this.pageDefinition.lpd_id
      //     )
      //   : null;
      // if (
      //   origDefinition &&
      //   (definitionJson !== origDefinition.definition ||
      //     this.pageDefinition.description !== origDefinition.description)
      // ) {
      //   if (confirm("Discard changes?")) {
      //     this.pageDefinition = null;
      //     this.showEditDefinition = false;
      //   }
      // } else {
      //   this.pageDefinition = null;
      //   this.showEditDefinition = false;
      // }
      this.pageDefinition = null;
      this.showEditDefinition = false;
    },
    configureDefinition() {
      this.showDefinitionEdit = true;
    },
    addTab() {
      const definition = this.pageDefinition?.definition;
      if (!definition) return;

      const newTab = {
        title: "New Tab",
        cols: [{
          cols: 0,
          rows: [],
        }],
      };
      if (!definition.tabs || definition.tabs.length === 0) {
        definition.tabs = [];
        newTab.rows = definition.rows;
        definition.rows = [];
      }
      definition.tabs.push(newTab);
    },
    tabEdit(tab) {
      this.tabModel = tab;
      this.showTabEdit = true;
    },
    tabMoveLeft(tab) {
      const tabs = this.pageDefinition?.definition?.tabs;
      if (!tabs) return;

      let currIdx = tabs.indexOf(tab);
      if (currIdx > 0)
        tabs.splice(currIdx - 1, 0, tabs.splice(currIdx, 1)[0]);
    },
    tabMoveRight(tab) {
      const tabs = this.pageDefinition?.definition?.tabs;
      if (!tabs) return;

      let currIdx = tabs.indexOf(tab);
      if (currIdx >= 0 && currIdx < tabs.length)
        tabs.splice(currIdx + 1, 0, tabs.splice(currIdx, 1)[0]);
    },
    deleteTab(tab) {
      const definition = this.pageDefinition?.definition;
      if (!definition) return;

      if (definition.tabs.length > 1) {
        const idx = definition.tabs.indexOf(tab);
        if (idx >= 0)
          definition.tabs.splice(idx, 1);
      }
    },
    changeActiveTab(tabIdx) {
      this.activeTab = tabIdx;
    },
    addColumn() {
      const definition = this.pageDefinition?.definition;
      if (!definition) return;

      const container = definition.tabs[this.activeTab];
      container.cols.push({
        cols: 0,
        rows: [],
      });
    },
    colEdit(col) {
      this.colModel = col;
      this.showColEdit = true;
    },
    deleteCol(col) {
      if (!this.activeTabDefinition) return;

      if (this.activeTabDefinition.cols.length > 1) {
        const idx = this.activeTabDefinition.cols.indexOf(col);
        if (idx >= 0)
          this.activeTabDefinition.cols.splice(idx, 1);
      }
    },
    rowEdit(row) {
      this.rowModel = row;
      this.showRowEdit = true;
    },
    addRow() {
      if (!this.activeTabDefinition) return;

      const newRow = {
        controlType: "LinkTiles",
        cols: 12,
        links: [],
        docListColumns: [],
      };
      this.addTile({ row: newRow });
      const container = this.activeTabDefinition.cols[this.activeTabDefinition.cols.length - 1];
      container.rows.push(newRow);
    },
    deleteRow(row, col) {
      if (!col || !col.rows) return;

      const idx = col.rows.indexOf(row);
      if (idx >= 0)
        col.rows.splice(idx, 1);
    },
    editTile(args) {
      this.rowModel = args.row;
      this.tileModel = args.link;
      this.showTileEdit = true;
    },
    addTile(args) {
      if (!args.row.links) this.$set(args.row, "links", []);
      const maxId = Math.max(0, ...args.row.links.map((l) => l.id || 0));
      const base = {
        id: maxId + 1,
        type: "link",
        title: "Untitled",
        url: "",
        icon: "",
        icon_colour: "gray",
        call_to_action: "",
        open_newtab: false,
        hero_image: null,
        image_width: null,
        image_height: null,
        tile_cols: null,
        sso: null,
      };
      if (args.row.controlType === "FeatureTiles") {
        Object.assign(base, {
          description: "",
          color: "blue",
          iconColor: null,
          icon: "star",
          tile_cols: 3,
        });
      }
      args.row.links.push(base);
    },
    // Stamp an endpoint preset's full wiring bundle onto a v3 chart row
    // (endpoint, method, mappings, filters, labels) — see endpointPresets.js.
    applyV3Preset(row, list, id) {
      this.$set(row, "preset", id || "");
      const preset = presetById(list, id);
      applyEndpointPreset(preset, (key, value) => this.$set(row, key, value));
    },
    addV3Tile(row) {
      if (!row.tiles) this.$set(row, "tiles", []);
      row.tiles.push({ label: "New tile", icon: "", destination: "", sub: "" });
    },
    addV3StatTile(row) {
      if (!row.tiles) this.$set(row, "tiles", []);
      row.tiles.push({ statField: "", label: "", icon: "", colour: "", to: "", docTypes: [], docTypesMode: "include", statuses: [], statusesMode: "include" });
    },
    removeV3StatTile(row, idx) {
      if (row.tiles) row.tiles.splice(idx, 1);
    },
    addV3FbItem(row) {
      if (!row.items) this.$set(row, "items", []);
      row.items.push({ title: "New item", detail: "", when: "", status: "submitted" });
    },
    removeV3FbItem(row, idx) {
      if (row.items) row.items.splice(idx, 1);
    },
    moveV3FbItem(row, idx, dir) {
      if (!row.items) return;
      const to = idx + dir;
      if (to < 0 || to >= row.items.length) return;
      row.items.splice(to, 0, row.items.splice(idx, 1)[0]);
    },
    addV3Link(row) {
      if (!row.links) this.$set(row, "links", []);
      row.links.push({ label: "New link", icon: "", destination: "" });
    },
    removeV3Link(row, idx) {
      if (row.links) row.links.splice(idx, 1);
    },
    addV3FeatTile(row) {
      if (!row.tiles) this.$set(row, "tiles", []);
      row.tiles.push({ title: "New card", desc: "", icon: "", tone: "accent", cta: "", destination: "" });
    },
    removeV3FeatTile(row, idx) {
      if (row.tiles) row.tiles.splice(idx, 1);
    },
    addV3Action(row) {
      if (!row.actions) this.$set(row, "actions", []);
      row.actions.push({ label: "New action", destination: "create", icon: "", sub: "", tone: "" });
    },
    removeV3Action(row, idx) {
      if (row.actions) row.actions.splice(idx, 1);
    },
    addV3Gap(row) {
      if (!row.gaps) this.$set(row, "gaps", []);
      row.gaps.push({ label: "New gap", count: 0, icon: "" });
    },
    removeV3Gap(row, idx) {
      if (row.gaps) row.gaps.splice(idx, 1);
    },
    // --- v3 visual editor (rows → columns → controls) ---
    // Ensure the active tab has the single full-width container that holds the
    // v3 rows, and return its `rows` array.
    v3Container() {
      const tab = this.activeTabDefinition;
      if (!tab) return null;
      if (!tab.cols || !tab.cols.length) this.$set(tab, "cols", [{ cols: 12, rows: [] }]);
      const container = tab.cols[0];
      if (!container.rows) this.$set(container, "rows", []);
      return container;
    },
    v3RebalanceSpans(row) {
      const n = row.cols.length || 1;
      const span = Math.max(1, Math.floor(12 / n));
      row.cols.forEach((c) => this.$set(c, "cols", span));
    },
    v3AddRow() {
      const container = this.v3Container();
      if (!container) return;
      container.rows.push({ controlType: "v3Group", cols: [{ cols: 12, rows: [] }] });
    },
    v3DeleteRow(row) {
      const container = this.v3Container();
      if (!container) return;
      container.rows = container.rows.filter((r) => r !== row);
    },
    v3AddColumn(row) {
      if (!row.cols) this.$set(row, "cols", []);
      row.cols.push({ cols: 6, rows: [] });
      this.v3RebalanceSpans(row);
    },
    v3DeleteColumn({ row, col }) {
      if (!row.cols || row.cols.length <= 1) return;
      row.cols = row.cols.filter((c) => c !== col);
      this.v3RebalanceSpans(row);
    },
    v3SetSpan({ col, value }) {
      this.$set(col, "cols", Math.min(12, Math.max(1, value || 1)));
    },
    v3AddControl(col) {
      if (!col.rows) this.$set(col, "rows", []);
      // Default to Stat Row (no required config); the settings dialog lets the
      // author switch the control type.
      const ctrl = { controlType: "v3StatRow", cols: 12, links: [] };
      col.rows.push(ctrl);
      this.rowEdit(ctrl);
    },
    v3DeleteControl({ col, ctrl }) {
      if (col.rows) col.rows = col.rows.filter((r) => r !== ctrl);
    },
    addV3Segment(row) {
      if (!row.segments) this.$set(row, "segments", []);
      row.segments.push({ label: "New segment", value: 0, tone: "" });
    },
    removeV3Segment(row, idx) {
      if (row.segments) row.segments.splice(idx, 1);
    },
    moveV3Segment(row, idx, dir) {
      if (!row.segments) return;
      const to = idx + dir;
      if (to < 0 || to >= row.segments.length) return;
      row.segments.splice(to, 0, row.segments.splice(idx, 1)[0]);
    },
    removeV3Tile(row, idx) {
      if (row.tiles) row.tiles.splice(idx, 1);
    },
    moveV3Tile(row, idx, dir) {
      if (!row.tiles) return;
      const to = idx + dir;
      if (to < 0 || to >= row.tiles.length) return;
      row.tiles.splice(to, 0, row.tiles.splice(idx, 1)[0]);
    },
    onControlTypeChange(newType) {
      if (!this.rowModel) return;
      if (newType === "FeatureTiles" && !this.rowModel.links) {
        this.$set(this.rowModel, "links", []);
      }
      if (newType === "v3FeedbackStrip") {
        if (!this.rowModel.items) this.$set(this.rowModel, "items", []);
      }
      if (newType === "v3TaskList") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 6);
      }
      if (newType === "v3TaskTable") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 8);
        if (!this.rowModel.scope) this.$set(this.rowModel, "scope", "all");
        if (this.rowModel.manager === undefined) this.$set(this.rowModel, "manager", false);
      }
      if (newType === "v3StatRow") {
        if (!this.rowModel.tiles) this.$set(this.rowModel, "tiles", []);
      }
      if (newType === "v3Delegated") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 6);
      }
      if (newType === "v3Insights") {
        if (this.rowModel.title === undefined) this.$set(this.rowModel, "title", "Anomalies & insights");
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 6);
      }
      if (newType === "v3Empty") {
        if (!this.rowModel.icon) this.$set(this.rowModel, "icon", "task_alt");
        if (!this.rowModel.title) this.$set(this.rowModel, "title", "You're all caught up");
        if (!this.rowModel.message)
          this.$set(this.rowModel, "message", "Nothing needs your attention right now.");
      }
      if (newType === "v3AdminPriority") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 3);
        if (!this.rowModel.scope) this.$set(this.rowModel, "scope", "all");
      }
      if (newType === "v3AttentionQueue") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 5);
        if (!this.rowModel.scope) this.$set(this.rowModel, "scope", "all");
      }
      if (newType === "v3Activity") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 8);
        if (!this.rowModel.days) this.$set(this.rowModel, "days", 30);
        if (!this.rowModel.scope) this.$set(this.rowModel, "scope", "all");
      }
      if (newType === "v3PriorityStack") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 3);
        if (this.rowModel.title === undefined) this.$set(this.rowModel, "title", "My Priorities");
      }
      if (newType === "v3ScoreRing") {
        if (this.rowModel.score === undefined) this.$set(this.rowModel, "score", 0);
        if (!this.rowModel.max) this.$set(this.rowModel, "max", 100);
        if (this.rowModel.label === undefined) this.$set(this.rowModel, "label", "Quality");
      }
      if (newType === "v3LinkList") {
        if (!this.rowModel.heading) this.$set(this.rowModel, "heading", "Go to");
        if (!this.rowModel.links) this.$set(this.rowModel, "links", []);
      }
      if (newType === "v3FeatureTiles") {
        if (!this.rowModel.tiles) this.$set(this.rowModel, "tiles", []);
      }
      if (newType === "v3DocList") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 8);
        if (!this.rowModel.docTypes) this.$set(this.rowModel, "docTypes", []);
      }
      if (newType === "v3DocListFull") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 12);
        if (!this.rowModel.docTypes) this.$set(this.rowModel, "docTypes", []);
        if (!this.rowModel.statuses) this.$set(this.rowModel, "statuses", []);
      }
      if (newType === "v3ActionPanel") {
        if (this.rowModel.title === undefined) this.$set(this.rowModel, "title", "Start levelling");
        if (!this.rowModel.actions) this.$set(this.rowModel, "actions", []);
        if (!this.rowModel.layout) this.$set(this.rowModel, "layout", "buttons");
      }
      if (newType === "v3Stepper") {
        if (!this.rowModel.steps)
          this.$set(this.rowModel, "steps", ["Upload JD", "Method", "Factor scoring", "Review", "Calibrate", "Accept"]);
        if (this.rowModel.current === undefined) this.$set(this.rowModel, "current", 2);
      }
      if (newType === "v3CoachToast") {
        if (!this.rowModel.icon) this.$set(this.rowModel, "icon", "mail");
        if (!this.rowModel.message)
          this.$set(this.rowModel, "message", "Invitations also arrive by email and take you straight into the evaluation.");
      }
      if (newType === "v3Calibration") {
        if (!this.rowModel.limit) this.$set(this.rowModel, "limit", 8);
      }
      if (newType === "v3Audit") {
        if (!this.rowModel.dataSource) this.$set(this.rowModel, "dataSource", "live");
        if (this.rowModel.subtitle === undefined) this.$set(this.rowModel, "subtitle", "EU Pay Transparency");
        if (this.rowModel.ready === undefined) this.$set(this.rowModel, "ready", 84);
        if (!this.rowModel.gaps)
          this.$set(this.rowModel, "gaps", [
            { label: "Missing rationale", count: 12, icon: "notes" },
            { label: "Missing comparators", count: 8, icon: "compare_arrows" },
            { label: "AI-assisted, unverified", count: 5, icon: "auto_awesome" },
          ]);
        if (this.rowModel.linkText === undefined) this.$set(this.rowModel, "linkText", "Open audit log");
      }
      if (newType === "v3DonutChart") {
        if (!this.rowModel.mode) this.$set(this.rowModel, "mode", "donut");
        if (!this.rowModel.dataSource) this.$set(this.rowModel, "dataSource", "static");
        if (!this.rowModel.segments) this.$set(this.rowModel, "segments", []);
        if (!this.rowModel.apiParams) this.$set(this.rowModel, "apiParams", []);
        if (!this.rowModel.apiFilters) this.$set(this.rowModel, "apiFilters", []);
        if (!this.rowModel.segmentColours) this.$set(this.rowModel, "segmentColours", []);
        if (!this.rowModel.segmentMapping)
          this.$set(this.rowModel, "segmentMapping", {
            labelField: "status",
            valueField: "count",
            colourField: "colour",
          });
      }
      if (newType === "DonutChart") {
        if (!this.rowModel.segments) this.$set(this.rowModel, "segments", []);
        if (!this.rowModel.dataSource) this.$set(this.rowModel, "dataSource", "static");
        if (!this.rowModel.apiParams) this.$set(this.rowModel, "apiParams", []);
        if (!this.rowModel.segmentMapping)
          this.$set(this.rowModel, "segmentMapping", {
            labelField: "label",
            valueField: "value",
            colourField: "colour",
          });
        if (!this.rowModel.segmentColours)
          this.$set(this.rowModel, "segmentColours", []);
        if (!this.rowModel.apiFilters)
          this.$set(this.rowModel, "apiFilters", []);
      }
      if (newType === "BarChart" || newType === "v3BarChart") {
        if (!this.rowModel.rows) this.$set(this.rowModel, "rows", []);
        if (!this.rowModel.filters) this.$set(this.rowModel, "filters", []);
        if (!this.rowModel.dataSource) this.$set(this.rowModel, "dataSource", "static");
        if (!this.rowModel.apiParams) this.$set(this.rowModel, "apiParams", []);
        if (!this.rowModel.rowMapping)
          this.$set(this.rowModel, "rowMapping", {
            labelField: "label",
            valueField: "value",
          });
      }
    },
    addDonutSegment() {
      if (!this.rowModel.segments) this.$set(this.rowModel, "segments", []);
      this.rowModel.segments.push({
        label: "New Segment",
        value: 0,
        color: this.hexColours[this.rowModel.segments.length % this.hexColours.length],
      });
    },
    addDonutColourOverride() {
      if (!this.rowModel.segmentColours)
        this.$set(this.rowModel, "segmentColours", []);
      this.rowModel.segmentColours.push({
        label: "",
        colour: this.hexColours[this.rowModel.segmentColours.length % this.hexColours.length],
      });
    },
    addDonutApiFilter() {
      if (!this.rowModel.apiFilters) this.$set(this.rowModel, "apiFilters", []);
      this.rowModel.apiFilters.push({ field: "docType", condition: "include", values: [] });
    },
    addBarRow() {
      if (!this.rowModel.rows) this.$set(this.rowModel, "rows", []);
      this.rowModel.rows.push({ label: "New Row", value: 0 });
    },
    addBarFilter() {
      if (!this.rowModel.filters) this.$set(this.rowModel, "filters", []);
      this.rowModel.filters.push({
        key: "",
        label: "",
        multiple: false,
        options: [],
        optionsEndpoint: "",
        optionsMethod: "GET",
        allItemsField: "",
        default: null,
      });
    },
    addApiParam(row) {
      if (!row.apiParams) this.$set(row, "apiParams", []);
      row.apiParams.push({ name: "", value: "" });
    },
    copyTile(args) {
      args.row.links.push(JSON.parse(JSON.stringify(args.link)));
    },
    deleteTile(args) {
      const idx = args.row.links.indexOf(args.link);
      if (idx >= 0)
        args.row.links.splice(idx, 1);
    },
    onDocListFieldSelect(col) {
      if (!col.text) {
        col.text =
          this.documentListFields.find((f) => f.value === col.value)?.text ||
          "";
      }
    },
    savePage() {
      this.page.page_route = this.page.page_route
        .replace(" ", "")
        .toLowerCase();

      const currentRoutes = this.$router
        .getRoutes()
        .filter(
          (r) => r.meta?.newUi && r.meta.landingPageId !== this.page.lp_id
        )
        .map((r) => r.name.toLowerCase());

      if (
        !this.page.page_route ||
        currentRoutes.includes(this.page.page_route)
      ) {
        this.response = {
          Status: "Error",
          Message: "Page route must be unique",
        };
        return;
      }

      let possibleError = false;
      this.isLoading = true;
      axios
        .post("admin/landingPageSave", {
          lp_id: this.page.lp_id,
          page_route: this.page.page_route,
          menu_section: this.page.menu_section,
          title: this.page.title,
          icon: this.page.icon,
          hide_header_create: this.page.hide_header_create,
          order: this.page.order,
          active: this.page.active,
        })
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            this.showPageEdit = false;
            if (!this.page.lp_id && resp.data.Data) {
              this.page.lp_id = resp.data.Data;
              this.landingPages.push(this.page);
            }

            this.landingPages.sort((a, b) => (a.order > b.order ? 1 : -1));
          }
          this.response = resp.data;
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    },
    saveDefinition() {
      let possibleError = false;
      const definitionJson = JSON.stringify(this.pageDefinition.definition);
      this.isLoading = true;
      axios
        .post("admin/landingPageDefinitionSave", {
          lp_id: this.pageDefinition.lp_id,
          lpd_id: this.pageDefinition.lpd_id,
          description: this.pageDefinition.description,
          definition: definitionJson,
        })
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            const origDefinition = this.page
              ? this.page.definitions.find(
                  (p) => p.lpd_id === this.pageDefinition.lpd_id
                )
              : null;
            if (origDefinition) {
              origDefinition.description = this.pageDefinition.description;
              origDefinition.active = this.pageDefinition.active;
              origDefinition.definition = definitionJson;
            }

            this.$loginState.updateLandingDefinition(
              this.pageDefinition.lpd_id,
              definitionJson
            );
          }
          this.response = resp.data;
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    },
    downloadDefinition(d, pd) {
      const definitionJson = JSON.stringify(pd);
      const fileName = `${d.title.replaceAll(
        " ",
        "_"
      )}-${pd.description.replaceAll(" ", "_")}.json`;
      utils.downloadFile(definitionJson, fileName, "text/json");
    },
    uploadDefinition(p, pd) {
      this.editDefinition(p, pd);
      this.$refs.inputFile.$refs.input.click();
    },
    uploadDefinitionFromFile(file) {
      if (file) {
        let reader = new FileReader();
        reader.addEventListener(
          "load",
          () => {
            const uploadedDefinition = JSON.parse(reader.result);
            this.pageDefinition.definition = this.prepDefinition(
              uploadedDefinition.definition
            );
            this.saveDefinition();
          },
          false
        );
        reader.readAsText(file, "UTF-8");
        this.$refs.inputFile.$refs.input.value = null;
      }
    },
    toggleRoute(pd, pgId) {
      let enabled = pd[`pg${pgId}_enabled`];

      if (enabled) {
        let possibleError = false;
        axios
          .post("admin/landingPageRemovePermission", {
            lpd_id: pd.lpd_id,
            pg_id: pgId,
          })
          .then((resp) => {
            possibleError = true;
            if (resp.data.Status === "OK") {
              pd[`pg${pgId}_enabled`] = false;
            }
            this.response = resp.data;
          })
          .catch((err) => {
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      } else {
        let possibleError = false;
        axios
          .post("admin/landingPageAddPermission", {
            lpd_id: pd.lpd_id,
            pg_id: pgId,
          })
          .then((resp) => {
            possibleError = true;
            if (resp.data.Status === "OK") {
              pd[`pg${pgId}_enabled`] = true;
            }
            this.response = resp.data;
          })
          .catch((err) => {
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/vars";

tr.inactive {
  background-color: #eee;
}

.draghandle {
  cursor: move;
}
</style>